/**
 * Localization strings for the preferences menu
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const preferencesText = createDictionary({
  preferences: {
    'en-us': 'Preferences',
    'ru-ru': 'Настройки',
    'es-es': 'Preferencias',
    'fr-fr': 'Préférences',
    'uk-ua': 'Уподобання',
    'de-ch': 'Einstellungen',
    'pt-br': 'Preferências',
    'hr-hr': 'Postavke',
    nb: 'Innstillinger',
  },
  customization: {
    'en-us': 'Customization',
    'ru-ru': 'Настройка',
    'es-es': 'Personalización',
    'fr-fr': 'Personnalisation',
    'uk-ua': 'Спеціальнізація',
    'de-ch': 'Anpassung',
    'pt-br': 'Personalização',
    'hr-hr': 'Prilagođavanje',
    nb: 'Tilpasning',
  },
  userPreferences: {
    'en-us': 'User Preferences',
    'ru-ru': 'Пользовательские настройки',
    'es-es': 'Preferencias del usuario',
    'fr-fr': "Préférences de l'utilisateur",
    'uk-ua': 'Налаштування користувача',
    'de-ch': 'Benutzereinstellungen',
    'pt-br': 'Preferências do usuário',
    'hr-hr': 'Korisničke postavke',
    nb: 'Brukerpreferanser',
  },
  defaultUserPreferences: {
    'en-us': 'Default User Preferences',
    'ru-ru': 'Настройки пользователя по умолчанию',
    'es-es': 'Preferencias de usuario predeterminadas',
    'fr-fr': 'Préférences utilisateur par défaut',
    'uk-ua': 'Параметри користувача за умовчанням',
    'de-ch': 'Standardbenutzereinstellungen',
    'pt-br': 'Preferências padrão do usuário',
    'hr-hr': 'Zadane korisničke postavke',
    nb: 'Standard brukerinnstillinger',
  },
  general: {
    'en-us': 'General',
    'ru-ru': 'Общий',
    'es-es': 'General',
    'fr-fr': 'Image personnalisée',
    'uk-ua': 'Спеціальне зображення',
    'de-ch': 'Allgemein',
    'pt-br': 'Em geral',
    'hr-hr': 'General',
    nb: 'General',
  },
  ui: {
    'en-us': 'User Interface',
    'ru-ru': 'Пользовательский интерфейс',
    'es-es': 'Interfaz de usuario',
    'fr-fr': 'Interface utilisateur',
    'uk-ua': 'Інтерфейс користувача',
    'de-ch': 'Benutzeroberfläche',
    'pt-br': 'Interface do usuário',
    'hr-hr': 'Korisničko sučelje',
    nb: 'Brukergrensesnitt',
  },
  theme: {
    'en-us': 'Theme',
    'ru-ru': 'Тема',
    'es-es': 'Tema',
    'fr-fr': 'Thème',
    'uk-ua': 'Тема',
    'de-ch': 'Thema',
    'pt-br': 'Tema',
    'hr-hr': 'Tema',
    nb: 'Tema',
  },
  useSystemSetting: {
    'en-us': 'Use system setting',
    'ru-ru': 'Использовать системные настройки',
    'es-es': 'Utilizar la configuración del sistema',
    'fr-fr': 'Utiliser les paramètres du système',
    'uk-ua': 'Використовуйте налаштування системи',
    'de-ch': 'Systemeinstellung verwenden',
    'pt-br': 'Usar as configurações do sistema',
    'hr-hr': 'Koristi sistemske postavke',
    nb: 'Bruk systeminnstillingen',
  },
  inheritOsSettings: {
    'en-us': 'Copies value from your Operating System settings',
    'ru-ru': 'Копирует значение из настроек вашей операционной системы.',
    'es-es': 'Copia el valor de la configuración de su sistema operativo',
    'fr-fr': "Copie la valeur des paramètres de votre système d'exploitation",
    'uk-ua': 'Копіює значення з налаштувань вашої операційної системи',
    'de-ch': 'Übernimmt den Wert aus Ihren Betriebssystemeinstellungen',
    'pt-br': 'Copia o valor das configurações do seu sistema operacional.',
    'hr-hr': 'Kopira vrijednost iz postavki vašeg operativnog sustava',
    nb: 'Kopierer verdi fra operativsysteminnstillingene dine',
  },
  light: {
    comment: 'Light mode',
    'en-us': 'Light',
    'ru-ru': 'Свет',
    'es-es': 'Claro',
    'fr-fr': 'Lumière',
    'uk-ua': 'світло',
    'de-ch': 'Hell',
    'pt-br': 'Luz',
    'hr-hr': 'Svjetlo',
    nb: 'Lys',
  },
  dark: {
    comment: 'Dark mode',
    'en-us': 'Dark',
    'ru-ru': 'Темный',
    'es-es': 'Oscuro',
    'fr-fr': 'Sombre',
    'uk-ua': 'Темний',
    'de-ch': 'Dunkel',
    'pt-br': 'Escuro',
    'hr-hr': 'Tamno',
    nb: 'Mørk',
  },
  reduceMotion: {
    'en-us': 'Reduce motion',
    'ru-ru': 'Уменьшите движение',
    'es-es': 'Reducir el movimiento',
    'fr-fr': 'Réduire les mouvements',
    'uk-ua': 'Зменшити рух',
    'de-ch': 'Bewegung reduzieren',
    'pt-br': 'Reduzir movimento',
    'hr-hr': 'Smanjite kretanje',
    nb: 'Reduser bevegelse',
  },
  reduceMotionDescription: {
    'en-us': 'Disable non-essential animations and transitions.',
    'ru-ru': 'Отключите несущественные анимации и переходы.',
    'es-es': 'Desactivar animaciones y transiciones no esenciales.',
    'fr-fr': 'Désactivez les animations et les transitions non essentielles.',
    'uk-ua': "Вимкніть необов'язкову анімацію та переходи.",
    'de-ch': 'Nicht erforderliche Animationen und Übergänge deaktivieren.',
    'pt-br': 'Desative animações e transições não essenciais.',
    'hr-hr': 'Onemogućite nebitne animacije i prijelaze.',
    nb: 'Deaktiver unødvendige animasjoner og overganger.',
  },
  rankThreshold: {
    'en-us': 'Show object count below',
    'de-ch': 'Objektanzahl unten anzeigen',
    'es-es': 'Mostrar recuento de objetos a continuación',
    'fr-fr': "Afficher le nombre d'objets ci-dessous",
    'pt-br': 'Mostrar a contagem de objetos abaixo',
    'ru-ru': 'Ниже показано количество объектов.',
    'uk-ua': "Показати кількість об'єктів нижче",
    'hr-hr': 'Prikaži broj objekata ispod',
    nb: 'Vis antall objekter nedenfor',
  },
  rankThresholdDescription: {
    'en-us':
      'Show the count of linked Collection Objects for all ranks below this one.',
    'ru-ru':
      'Показывать количество объектов коллекции для всех рангов, включая и ниже этого.',
    'es-es':
      'Mostrar la cantidad de objetos de colección para todos los rangos, incluidos y por debajo de este.',
    'fr-fr':
      "Afficher le nombre d'objets de collection pour tous les rangs, y compris et en dessous de celui-ci.",
    'uk-ua':
      "Показати кількість об'єктів колекції для всіх рангів, включаючи та нижче цього.",
    'de-ch':
      'Anzahl der Sammlungsobjekte für alle Ränge einschließlich und unterhalb dieses Rangs anzeigen.',
    'pt-br':
      'Mostrar a contagem de Objetos de Coleção para todos os ranks, incluindo e abaixo deste.',
    'hr-hr':
      'Prikaži broj povezanih objekata kolekcije za sve rangove ispod ovog.',
    nb: 'Vis antallet lenkede samlingsobjekter for alle ranger under denne.',
  },
  reduceTransparency: {
    'en-us': 'Reduce transparency',
    'ru-ru': 'Снизить прозрачность',
    'es-es': 'Reducir la transparencia',
    'fr-fr': 'Réduire la transparence',
    'uk-ua': 'Зменшити прозорість',
    'de-ch': 'Transparenz reduzieren',
    'pt-br': 'Reduzir a transparência',
    'hr-hr': 'Smanjite transparentnost',
    nb: 'Reduser gjennomsiktigheten',
  },
  reduceTransparencyDescription: {
    'en-us':
      'Whether to disable translucent backgrounds for user interface components whenever possible (e.g. table headers in tree view).',
    'ru-ru':
      'Следует ли по возможности отключать полупрозрачный фон для компонентов пользовательского интерфейса (например, заголовки таблиц в древовидном представлении).',
    'es-es':
      'Si se deben deshabilitar los fondos translúcidos para los componentes de la interfaz de usuario siempre que sea posible (por ejemplo, encabezados de tabla en la vista de árbol).',
    'fr-fr':
      "S'il faut désactiver les arrière-plans translucides pour les composants de l'interface utilisateur chaque fois que possible (par exemple, les en-têtes de tableau dans l'arborescence).",
    'uk-ua':
      'Чи вимикати напівпрозорий фон для компонентів інтерфейсу користувача, коли це можливо (наприклад, заголовки таблиць у перегляді дерева).',
    'de-ch':
      'Durchsichtige Hintergründe für Benutzeroberflächenkomponenten wann immer möglich deaktivieren (z. B. Tabellenüberschriften in der Baumansicht).',
    'pt-br':
      'Desativar ou não os fundos translúcidos para componentes da interface do usuário sempre que possível (por exemplo, cabeçalhos de tabela em uma visualização em árvore).',
    'hr-hr':
      'Treba li onemogućiti prozirne pozadine za komponente korisničkog sučelja kad god je to moguće (npr. zaglavlja tablica u prikazu stabla).',
    nb: 'Om gjennomskinnelige bakgrunner for brukergrensesnittkomponenter skal deaktiveres når det er mulig (f.eks. tabelloverskrifter i trevisning).',
  },
  contrast: {
    'en-us': 'Contrast',
    'ru-ru': 'Контраст',
    'es-es': 'Contraste',
    'fr-fr': 'Contraste',
    'uk-ua': 'Контраст',
    'de-ch': 'Kontrast',
    'pt-br': 'Contraste',
    'hr-hr': 'Kontrast',
    nb: 'Kontrast',
  },
  increase: {
    'en-us': 'Increase',
    'ru-ru': 'Увеличивать',
    'es-es': 'Aumentar',
    'fr-fr': 'Augmenter',
    'uk-ua': 'Збільшити',
    'de-ch': 'Erhöhen',
    'pt-br': 'Aumentar',
    'hr-hr': 'Povećati',
    nb: 'Øke',
  },
  reduce: {
    'en-us': 'Reduce',
    'ru-ru': 'Уменьшать',
    'es-es': 'Reducir',
    'fr-fr': 'Réduire',
    'uk-ua': 'Зменшити',
    'de-ch': 'Verringern',
    'pt-br': 'Reduzir',
    'hr-hr': 'Smanjiti',
    nb: 'Redusere',
  },
  noPreference: {
    'en-us': 'No preference',
    'ru-ru': 'Без предпочтений',
    'es-es': 'Sin preferencia',
    'fr-fr': 'Pas de préférence',
    'uk-ua': 'Без переваг',
    'de-ch': 'Keine Präferenz',
    'pt-br': 'Sem preferência',
    'hr-hr': 'Bez preferencija',
    nb: 'Ingen preferanse',
  },
  fontSize: {
    'en-us': 'Font size',
    'ru-ru': 'размер шрифта',
    'es-es': 'Tamaño de fuente',
    'fr-fr': 'Taille de police',
    'uk-ua': 'Розмір шрифту',
    'de-ch': 'Schriftgrösse',
    'pt-br': 'Tamanho da fonte',
    'hr-hr': 'Veličina fonta',
    nb: 'Skriftstørrelse',
  },
  fontFamily: {
    'en-us': 'Font family',
    'ru-ru': 'Семейство шрифтов',
    'es-es': 'Familia de fuentes',
    'fr-fr': 'Famille de polices',
    'uk-ua': 'Сімейство шрифтів',
    'de-ch': 'Schrift-Familie',
    'pt-br': 'Família de fontes',
    'hr-hr': 'Obitelj fontova',
    nb: 'Skriftfamilie',
  },
  fontFamilyDescription: {
    'en-us':
      'You can specify any font that is on your computer, even if it is not in the list. A comma-separated list of fonts is also supported, where each subsequent font will be used if the previous one is not available.',
    'ru-ru':
      'Вы можете указать любой шрифт, имеющийся на вашем компьютере, даже если его нет в списке. Поддерживается также список шрифтов, разделенных запятыми, при этом будет использоваться каждый последующий шрифт, если предыдущий недоступен.',
    'es-es':
      'Puede especificar cualquier fuente de su ordenador, incluso si no está en la lista. También se admite una lista de fuentes separadas por comas, donde se usará cada fuente subsiguiente si la anterior no está disponible.',
    'fr-fr':
      "Vous pouvez spécifier n'importe quelle police installée sur votre ordinateur, même si elle ne figure pas dans la liste. Les listes de polices séparées par des virgules sont également prises en charge ; si la police précédente est indisponible, la suivante sera utilisée.",
    'uk-ua':
      "Ви можете вказати будь-який шрифт, який є на вашому комп'ютері, навіть якщо його немає в списку. Також підтримується розділений комами список шрифтів, у якому використовуватиметься другий шрифт, якщо перший недоступний тощо.",
    'de-ch':
      'Sie können jede Schriftart angeben, die sich auf Ihrem Computer befindet, auch wenn diese nicht in der Liste enthalten ist. Eine durch Kommas getrennte Liste von Schriftarten wird ebenfalls unterstützt, wobei die zweite Schriftart verwendet wird, wenn die erste nicht verfügbar ist usw.',
    'pt-br':
      'Você pode especificar qualquer fonte que esteja instalada no seu computador, mesmo que ela não esteja na lista. Também é possível usar uma lista de fontes separadas por vírgulas, em que cada fonte subsequente será usada caso a anterior não esteja disponível.',
    'hr-hr':
      'Možete odrediti bilo koji font koji se nalazi na vašem računalu, čak i ako nije na popisu. Također je podržan popis fontova odvojen zarezima, gdje će se svaki sljedeći font koristiti ako prethodni nije dostupan.',
    nb: 'Du kan angi hvilken som helst skrifttype som finnes på datamaskinen din, selv om den ikke er i listen. En kommaseparert liste over skrifter støttes også, der hver påfølgende skrifttype vil bli brukt hvis den forrige ikke er tilgjengelig.',
  },
  defaultFont: {
    'en-us': '(default font)',
    'ru-ru': '(шрифт по умолчанию)',
    'es-es': '(fuente predeterminada)',
    'fr-fr': '(police par défaut)',
    'uk-ua': '(типовий шрифт)',
    'de-ch': '(Standardschriftart)',
    'pt-br': '(fonte padrão)',
    'hr-hr': '(zadani font)',
    nb: '(standardfont)',
  },
  maxFormWidth: {
    'en-us': 'Max form width',
    'ru-ru': 'Максимальная ширина формы',
    'es-es': 'Ancho máximo del formulario',
    'fr-fr': 'Largeur maximale du formulaire',
    'uk-ua': 'Максимальна ширина форми',
    'de-ch': 'Maximale Formularbreite',
    'pt-br': 'Largura máxima do formulário',
    'hr-hr': 'Maksimalna širina obrasca',
    nb: 'Maksimal skjemabredde',
  },
  fieldBackgrounds: {
    'en-us': 'Field backgrounds',
    'ru-ru': 'Полевые исследования',
    'es-es': 'Fondos de campo',
    'fr-fr': 'Milieux de terrain',
    'uk-ua': 'Польові фони',
    'de-ch': 'Feldhintergründe',
    'pt-br': 'Histórico de campo',
    'hr-hr': 'Pozadina polja',
    nb: 'Feltbakgrunner',
  },
  fieldBackground: {
    'en-us': 'Field background',
    'ru-ru': 'Полевой фон',
    'es-es': 'Fondo de campo',
    'fr-fr': 'Contexte du terrain',
    'uk-ua': 'Поле фону',
    'de-ch': 'Feldhintergrund',
    'pt-br': 'Histórico de campo',
    'hr-hr': 'Pozadina polja',
    nb: 'Feltbakgrunn',
  },
  disabledFieldBackground: {
    'en-us': 'Disabled field background',
    'ru-ru': 'Фоновое поле инвалидности',
    'es-es': 'Fondo de campo deshabilitado',
    'fr-fr': 'Fond de champ désactivé',
    'uk-ua': 'Вимкнений фон поля',
    'de-ch': 'Deaktivierter Feldhintergrund',
    'pt-br': 'fundo de campo desativado',
    'hr-hr': 'Pozadina polja za osobe s invaliditetom',
    nb: 'Bakgrunn for deaktivert felt',
  },
  invalidFieldBackground: {
    'en-us': 'Invalid field background',
    'ru-ru': 'Недопустимое поле фона',
    'es-es': 'Fondo de campo no válido',
    'fr-fr': 'Fond de champ invalide',
    'uk-ua': 'Недійсний фон поля',
    'de-ch': 'Ungültiger Feldhintergrund',
    'pt-br': 'Fundo de campo inválido',
    'hr-hr': 'Nevažeća pozadina polja',
    nb: 'Ugyldig feltbakgrunn',
  },
  requiredFieldBackground: {
    'en-us': 'Required field background',
    'ru-ru': 'Требуемый опыт работы в данной области',
    'es-es': 'Antecedentes de campo requeridos',
    'fr-fr': 'Contexte du champ obligatoire',
    'uk-ua': "Обов'язковий фон поля",
    'de-ch': 'Feldhintergrund erforderlich',
    'pt-br': 'Campo obrigatório de fundo',
    'hr-hr': 'Pozadina obaveznog polja',
    nb: 'Bakgrunn for obligatoriske felt',
  },
  darkFieldBackground: {
    'en-us': 'Field background (dark theme)',
    'ru-ru': 'Фоновое изображение поля (темная тема)',
    'es-es': 'Fondo de campo (tema oscuro)',
    'fr-fr': 'Fond de champ (thème sombre)',
    'uk-ua': 'Фон поля (темна тема)',
    'de-ch': 'Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo (tema escuro)',
    'hr-hr': 'Pozadina polja (tamna tema)',
    nb: 'Feltbakgrunn (mørkt tema)',
  },
  darkDisabledFieldBackground: {
    'en-us': 'Disabled field background (dark theme)',
    'ru-ru': 'Фоновое изображение поля отключено (темная тема).',
    'es-es': 'Fondo de campo deshabilitado (tema oscuro)',
    'fr-fr': 'Fond de champ désactivé (thème sombre)',
    'uk-ua': 'Вимкнений фон поля (темна тема)',
    'de-ch': 'Deaktivierter Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo desativado (tema escuro)',
    'hr-hr': 'Pozadina onemogućenog polja (tamna tema)',
    nb: 'Bakgrunn for deaktivert felt (mørkt tema)',
  },
  darkInvalidFieldBackground: {
    'en-us': 'Invalid field background (dark theme)',
    'ru-ru': 'Неверный фон поля (темная тема)',
    'es-es': 'Fondo de campo no válido (tema oscuro)',
    'fr-fr': 'Largeur de colonne de grille de sous-vue flexible',
    'uk-ua': 'Гнучка ширина стовпця сітки вкладеного перегляду',
    'de-ch': 'Ungültiger Feldhintergrund (Dunkles Thema)',
    'pt-br': 'Fundo de campo inválido (tema escuro)',
    'hr-hr': 'Nevažeća pozadina polja (tamna tema)',
    nb: 'Ugyldig feltbakgrunn (mørkt tema)',
  },
  darkRequiredFieldBackground: {
    'en-us': 'Required field background (dark theme)',
    'ru-ru': 'Фоновое изображение обязательного поля (темная тема).',
    'es-es': 'Campo de fondo obligatorio (tema oscuro)',
    'fr-fr': 'Fond de champ obligatoire (thème sombre)',
    'uk-ua': 'Обов’язковий фон поля (темна тема)',
    'de-ch': 'Feldhintergrund erforderlich (Dunkles Thema)',
    'pt-br': 'Fundo de campo obrigatório (tema escuro)',
    'hr-hr': 'Pozadina obaveznog polja (tamna tema)',
    nb: 'Bakgrunn for obligatorisk felt (mørkt tema)',
  },
  dialogs: {
    'en-us': 'Dialogs',
    'ru-ru': 'Диалоги',
    'es-es': 'Diálogos',
    'fr-fr': 'Boîtes de dialogue',
    'uk-ua': 'Діалоги',
    'de-ch': 'Dialoge',
    'pt-br': 'Diálogos',
    'hr-hr': 'Dijalozi',
    nb: 'Dialoger',
  },
  appearance: {
    'en-us': 'Appearance',
    'ru-ru': 'Появление',
    'es-es': 'Apariencia',
    'fr-fr': 'Apparence',
    'uk-ua': 'Зовнішній вигляд',
    'de-ch': 'Aussehen',
    'pt-br': 'Aparência',
    'hr-hr': 'Izgled',
    nb: 'Utseende',
  },
  buttonsLight: {
    'en-us': 'Buttons (light mode)',
    'de-ch': 'Buttons (Helles Thema)',
    'es-es': 'Botones (modo luz)',
    'fr-fr': 'Boutons (mode lumière)',
    'ru-ru': 'Кнопки (световой режим)',
    'uk-ua': 'Кнопки (світлий режим)',
    'pt-br': 'Botões (modo claro)',
    'hr-hr': 'Tipke (svjetlosni način rada)',
    nb: 'Knapper (lysmodus)',
  },
  buttonsDark: {
    'en-us': 'Buttons (dark mode)',
    'de-ch': 'Buttons (Dunkles Thema)',
    'es-es': 'Botones (modo oscuro)',
    'fr-fr': 'Boutons (mode sombre)',
    'ru-ru': 'Кнопки (темный режим)',
    'uk-ua': 'Кнопки (темний режим)',
    'pt-br': 'Botões (modo escuro)',
    'hr-hr': 'Gumbi (tamni način rada)',
    nb: 'Knapper (mørk modus)',
  },
  translucentDialog: {
    'en-us': 'Translucent dialogs',
    'ru-ru': 'Полупрозрачные диалоги',
    'es-es': 'Diálogos translúcidos',
    'fr-fr': 'Dialogues translucides',
    'uk-ua': 'Напівпрозорі діалоги',
    'de-ch': 'Durchscheinende Dialoge',
    'pt-br': 'Diálogos translúcidos',
    'hr-hr': 'Prozirni dijalozi',
    nb: 'Gjennomsiktige dialoger',
  },
  translucentDialogDescription: {
    'en-us': 'Whether dialogs have translucent background.',
    'ru-ru': 'Есть ли у диалогов полупрозрачный фон.',
    'es-es': 'Si los diálogos tienen fondo translúcido.',
    'fr-fr': 'Si les boîtes de dialogue ont un fond translucide.',
    'uk-ua': 'Чи мають діалоги прозорий фон.',
    'de-ch': 'Dialogfenster mit durchscheinenden Hintergrund.',
    'pt-br': 'Se os diálogos têm fundo translúcido.',
    'hr-hr': 'Imaju li dijalozi prozirnu pozadinu.',
    nb: 'Om dialoger har gjennomsiktig bakgrunn.',
  },
  alwaysPrompt: {
    'en-us': 'Always prompt to choose collection',
    'ru-ru': 'Всегда оперативно выбирайте коллекцию.',
    'es-es': 'Siempre solicitando elegir la colección',
    'fr-fr': 'Toujours invité à choisir la collection',
    'uk-ua': 'Завжди підкажуть вибрати колекцію',
    'de-ch': 'Immer zur Auswahl der Sammlung auffordern',
    'pt-br': 'Sempre solicitando a escolha da coleção',
    'hr-hr': 'Uvijek se traži odabir kolekcije',
    nb: 'Alltid oppfordret til å velge samling',
  },
  treeEditor: {
    'en-us': 'Tree Editor',
    'ru-ru': 'Редактор дерева',
    'es-es': 'Editor de árboles',
    'fr-fr': "Éditeur d'arborescence",
    'uk-ua': 'Редактор дерева',
    'de-ch': 'Baumeditor',
    'pt-br': 'Editor de Árvores',
    'hr-hr': 'Uređivač stabla',
    nb: 'Treredigerer',
  },
  treeAccentColor: {
    'en-us': 'Tree accent color',
    'ru-ru': 'Цвет акцента дерева',
    'es-es': 'Color de acento del árbol',
    'fr-fr': "Couleur d'accent d'arbre",
    'uk-ua': 'Колір акценту дерева',
    'de-ch': 'Baumakzentfarbe',
    'pt-br': 'cor de destaque da árvore',
    'hr-hr': 'Boja naglaska na drvetu',
    nb: 'Aksentfarge for treet',
  },
  synonymColor: {
    'en-us': 'Synonym color',
    'ru-ru': 'Синоним цвета',
    'es-es': 'color sinónimo',
    'fr-fr': 'Synonyme couleur',
    'uk-ua': 'Синонім кольору',
    'de-ch': 'Synonymfarbe',
    'pt-br': 'Cor (sinônimo)',
    'hr-hr': 'Sinonimna boja',
    nb: 'Synonymfarge',
  },
  showNewDataSetWarning: {
    'en-us': 'Show new Data Set warning',
    'ru-ru': 'Показать новое предупреждение о наборе данных',
    'es-es': 'Mostrar nueva advertencia de conjunto de datos',
    'fr-fr': "Afficher un nouvel avertissement sur l'ensemble de données",
    'uk-ua': 'Показати попередження про новий набір даних',
    'de-ch': 'Warnung für neuen Datensatz anzeigen',
    'pt-br': 'Exibir aviso de novo conjunto de dados',
    'hr-hr': 'Prikaži upozorenje o novom skupu podataka',
    nb: 'Vis advarsel om nytt datasett',
  },
  showNewDataSetWarningDescription: {
    'en-us': 'Show an informational message when creating a new Data Set.',
    'ru-ru':
      'При создании нового набора данных отображать информационное сообщение.',
    'es-es':
      'Mostrar un mensaje informativo al crear un nuevo conjunto de datos.',
    'fr-fr':
      "Afficher un message d'information lors de la création d'un nouvel ensemble de données.",
    'uk-ua':
      'Показувати інформаційне повідомлення під час створення нового набору даних.',
    'de-ch': 'Zeige eine Meldung beim erstellen eines neuen Datensatzes an.',
    'pt-br':
      'Exibir uma mensagem informativa ao criar um novo conjunto de dados.',
    'hr-hr':
      'Prikaži informativnu poruku prilikom izrade novog skupa podataka.',
    nb: 'Vis en informasjonsmelding når du oppretter et nytt datasett.',
  },
  header: {
    'en-us': 'Navigation Menu',
    'ru-ru': 'Навигационное меню',
    'es-es': 'Menú de navegación',
    'fr-fr': 'le menu de navigation',
    'uk-ua': 'Навігаційне меню',
    'de-ch': 'Navigationsmenü',
    'pt-br': 'Menu de navegação',
    'hr-hr': 'Navigacijski izbornik',
    nb: 'Navigasjonsmeny',
  },
  application: {
    'en-us': 'Application',
    'ru-ru': 'Приложение',
    'es-es': 'Solicitud',
    'fr-fr': 'Application',
    'uk-ua': 'застосування',
    'de-ch': 'Anwendung',
    'pt-br': 'Aplicativo',
    'hr-hr': 'Primjena',
    nb: 'Søknad',
  },
  allowDismissingErrors: {
    'en-us': 'Allow dismissing error messages',
    'ru-ru': 'Разрешить закрытие сообщений об ошибках',
    'es-es': 'Permitir descartar mensajes de error',
    'fr-fr': "Autoriser le rejet des messages d'erreur",
    'uk-ua': 'Дозволити закривати повідомлення про помилки',
    'de-ch': 'Erlaube das Verwerfen von Fehlermeldungen',
    'pt-br': 'Permitir descartar mensagens de erro',
    'hr-hr': 'Dopusti odbacivanje poruka o pogreškama',
    nb: 'Tillat avvisning av feilmeldinger',
  },
  updatePageTitle: {
    'en-us': 'Update page title',
    'ru-ru': 'Обновить заголовок страницы',
    'es-es': 'Actualizar el título de la página',
    'fr-fr': 'Mettre à jour le titre de la page',
    'uk-ua': 'Оновити назву сторінки',
    'de-ch': 'Seitentitel aktualisieren',
    'pt-br': 'Atualizar título da página',
    'hr-hr': 'Ažuriraj naslov stranice',
    nb: 'Oppdater sidetittel',
  },
  updatePageTitleDescription: {
    'en-us':
      "Whether to update the title of the page to match dialog's header.",
    'ru-ru':
      'Следует ли обновлять заголовок страницы в соответствии с заголовком диалогового окна.',
    'es-es':
      'Si se debe actualizar el título de la página para que coincida con el encabezado del cuadro de diálogo.',
    'fr-fr':
      "S'il faut mettre à jour le titre de la page pour qu'il corresponde à l'en-tête de la boîte de dialogue.",
    'uk-ua':
      'Чи оновлювати назву сторінки відповідно до заголовка діалогового вікна.',
    'de-ch':
      'Titel der Seite so aktualisieren, dass er mit der Kopfzeile des Dialogs übereinstimmt.',
    'pt-br':
      'Se o título da página deve ser atualizado para corresponder ao cabeçalho da caixa de diálogo.',
    'hr-hr':
      'Treba li ažurirati naslov stranice kako bi odgovarao zaglavlju dijaloga.',
    nb: 'Om sidetittelen skal oppdateres slik at den samsvarer med overskriften i dialogboksen.',
  },
  updatePageTitleFormDescription: {
    'en-us': 'Whether to update the title of the page to match current record.',
    'ru-ru':
      'Следует ли обновить заголовок страницы в соответствии с текущей записью.',
    'es-es':
      'Si se debe actualizar el título de la página para que coincida con el registro actual.',
    'fr-fr':
      "S'il faut mettre à jour le titre de la page pour qu'il corresponde à l'enregistrement actuel.",
    'uk-ua': 'Чи оновлювати назву сторінки відповідно до поточного запису.',
    'de-ch':
      'Titel der Seite aktualisieren, damit er mit dem aktuellen Datensatz übereinstimmt.',
    'pt-br':
      'Se o título da página deve ser atualizado para corresponder ao registro atual.',
    'hr-hr':
      'Treba li ažurirati naslov stranice kako bi odgovarao trenutnom zapisu.',
    nb: 'Om sidetittelen skal oppdateres slik at den samsvarer med gjeldende oppføring.',
  },
  queryComboBox: {
    'en-us': 'Query Combo Box',
    'ru-ru': 'Выпадающий список запросов',
    'es-es': 'Cuadro combinado de consulta',
    'uk-ua': 'Поле зі списком запитів',
    'de-ch': 'Abfrage-Kombinationsfeld',
    'fr-fr': 'Zone de liste déroulante de requête',
    'pt-br': 'Caixa de combinação de consulta',
    'hr-hr': 'Kombinirani okvir upita',
    nb: 'Søkekombinasjonsboks',
  },
  searchAlgorithm: {
    'en-us': 'Search Algorithm',
    'ru-ru': 'Алгоритм поиска',
    'es-es': 'Algoritmo de búsqueda',
    'fr-fr': 'Algorithme de recherche',
    'uk-ua': 'Алгоритм пошуку',
    'de-ch': 'Suchalgorithmus',
    'pt-br': 'Algoritmo de Busca',
    'hr-hr': 'Algoritam pretraživanja',
    nb: 'Søkealgoritme',
  },
  treeSearchAlgorithm: {
    'en-us': 'Search Algorithm (for relationships with tree tables)',
    'ru-ru':
      'Алгоритм поиска (для установления связей с помощью древовидных таблиц)',
    'es-es': 'Algoritmo de búsqueda (para relaciones con tablas de árbol)',
    'fr-fr':
      'Algorithme de recherche (pour les relations avec les tables arborescentes)',
    'uk-ua': 'Алгоритм пошуку (для зв’язків із деревоподібними таблицями)',
    'de-ch': 'Suchalgorithmus (für Beziehungen mit Baumtabellen)',
    'pt-br': 'Algoritmo de busca (para relacionamentos com tabelas em árvore)',
    'hr-hr': 'Algoritam pretraživanja (za odnose s tablicama stabla)',
    nb: 'Søkealgoritme (for relasjoner med tretabeller)',
  },
  startsWithInsensitive: {
    'en-us': 'Starts With (case-insensitive)',
    'ru-ru': 'Начинается с (регистр не учитывается)',
    'es-es': 'Comienza con (sin distinción de mayúsculas y minúsculas)',
    'fr-fr': 'Commence par (insensible à la casse)',
    'uk-ua': 'Починається з (без урахування регістру)',
    'de-ch': 'Beginnt mit (Groß-/Kleinschreibung wird nicht beachtet)',
    'pt-br': 'Começa com (sem distinção entre maiúsculas e minúsculas)',
    'hr-hr': 'Počinje s (ne razlikuje velika i mala slova)',
    nb: 'Starter med (ikke skille mellom store og små bokstaver)',
  },
  startsWithDescription: {
    'en-us': 'Search for values that begin with a given query string.',
    'ru-ru': 'Найдите значения, начинающиеся с заданной поисковой строки.',
    'es-es':
      'Buscar valores que comiencen con una cadena de consulta determinada.',
    'fr-fr':
      'Rechercher des valeurs commençant par une chaîne de requête donnée.',
    'uk-ua': 'Пошук значень, які починаються з заданого рядка запиту.',
    'de-ch':
      'Suche nach Werten, die mit einer bestimmten Suchanfrage beginnen.',
    'pt-br':
      'Pesquise valores que comecem com uma determinada sequência de consulta.',
    'hr-hr': 'Traži vrijednosti koje počinju zadanim nizom upita.',
    nb: 'Søk etter verdier som begynner med en gitt spørrestreng.',
  },
  startsWithCaseSensitive: {
    'en-us': 'Starts With (case-sensitive)',
    'ru-ru': 'Начинается с (регистр имеет значение)',
    'es-es': 'Comienza con (distingue mayúsculas y minúsculas)',
    'fr-fr': 'Commence par (sensible à la casse)',
    'uk-ua': 'Починається з (з урахуванням регістру)',
    'de-ch': 'Beginnt mit (Groß-/Kleinschreibung beachten)',
    'pt-br': 'Começa com (diferencia maiúsculas de minúsculas)',
    'hr-hr': 'Počinje s (razlikuje velika i mala slova)',
    nb: 'Starter med (skiller mellom store og små bokstaver)',
  },
  startsWithCaseSensitiveDescription: {
    'en-us': 'Search for values that begin with a given query string.',
    'ru-ru': 'Найдите значения, начинающиеся с заданной поисковой строки.',
    'es-es':
      'Buscar valores que comiencen con una cadena de consulta determinada.',
    'fr-fr':
      'Recherchez les valeurs qui commencent par une chaîne de requête donnée.',
    'uk-ua': 'Пошук значень, які починаються з заданого рядка запиту.',
    'de-ch':
      'Suche nach Werten, die mit einer bestimmten Suchanfrage beginnen.',
    'pt-br':
      'Pesquise valores que comecem com uma determinada sequência de consulta.',
    'hr-hr': 'Traži vrijednosti koje počinju zadanim nizom upita.',
    nb: 'Søk etter verdier som begynner med en gitt spørrestreng.',
  },
  containsInsensitive: {
    'en-us': 'Contains (case-insensitive)',
    'ru-ru': 'Содержит (регистр нечувствителен)',
    'es-es': 'Contiene (sin distinción de mayúsculas y minúsculas)',
    'fr-fr': 'Contient (insensible à la casse)',
    'uk-ua': 'Містить (незалежно від регістру)',
    'de-ch': 'Enthält (Groß-/Kleinschreibung wird nicht beachtet)',
    'pt-br': 'Contém (sem distinção entre maiúsculas e minúsculas)',
    'hr-hr': 'Sadrži (ne razlikuje velika i mala slova)',
    nb: 'Inneholder (ikke skille mellom store og små bokstaver)',
  },
  containsCaseSensitive: {
    'en-us': 'Contains (case-sensitive)',
    'ru-ru': 'Содержит (с учетом регистра)',
    'es-es': 'Contiene (sensible a mayúsculas y minúsculas)',
    'fr-fr': 'Contient (sensible à la casse)',
    'uk-ua': 'Містить (з урахуванням регістру)',
    'de-ch': 'Enthält (Groß-/Kleinschreibung beachten)',
    'pt-br': 'Contém (diferencia maiúsculas de minúsculas)',
    'hr-hr': 'Sadrži (osjetljivo na velika i mala slova)',
    nb: 'Inneholder (skiller mellom store og små bokstaver)',
  },
  containsDescription: {
    'en-us':
      'Search for values that contain a given query string (case-insensitive).',
    'ru-ru':
      'Поиск значений, содержащих заданную строку запроса (регистр не учитывается).',
    'es-es':
      'Buscar valores que contengan una cadena de consulta determinada (sin distinción entre mayúsculas y minúsculas).',
    'uk-ua':
      'Пошук значень, які містять заданий рядок запиту (незалежно від регістру).',
    'de-ch':
      'Suche nach Werten, die eine bestimmte Suchanfrage enthalten (Groß-/Kleinschreibung wird nicht beachtet).',
    'fr-fr':
      'Recherchez les valeurs contenant une chaîne de requête donnée (insensible à la casse).',
    'pt-br':
      'Pesquise valores que contenham uma determinada string de consulta (sem distinção entre maiúsculas e minúsculas).',
    'hr-hr':
      'Traži vrijednosti koje sadrže zadani niz upita (ne razlikuje velika i mala slova).',
    nb: 'Søk etter verdier som inneholder en gitt spørrestreng (ikke skille mellom store og små bokstaver).',
  },
  containsCaseSensitiveDescription: {
    'en-us':
      'Search for values that contain a given query string (case-sensitive).',
    'ru-ru':
      'Поиск значений, содержащих заданную строку запроса (с учетом регистра).',
    'es-es':
      'Buscar valores que contengan una cadena de consulta determinada (distingue entre mayúsculas y minúsculas).',
    'fr-fr':
      'Recherchez les valeurs contenant une chaîne de requête donnée (sensible à la casse).',
    'uk-ua':
      'Пошук значень, які містять заданий рядок запиту (з урахуванням регістру).',
    'de-ch':
      'Suche nach Werten, die eine bestimmte Suchanfrage enthalten (Groß-/Kleinschreibung beachten).',
    'pt-br':
      'Pesquise valores que contenham uma determinada string de consulta (diferenciando maiúsculas de minúsculas).',
    'hr-hr':
      'Traži vrijednosti koje sadrže zadani niz upita (osjetljivo na velika i mala slova).',
    nb: 'Søk etter verdier som inneholder en gitt spørrestreng (små- og store-/småskala-følsom).',
  },
  containsSecondDescription: {
    'en-us':
      'Can use _ to match any single character or % to match any number of characters.',
    'ru-ru':
      'Символ _ можно использовать для сопоставления одного символа, а символ % — для сопоставления любого количества символов.',
    'es-es':
      'Se puede usar _ para que coincida con cualquier carácter individual o % para que coincida con cualquier número de caracteres.',
    'fr-fr':
      "Peut utiliser _ pour correspondre à n'importe quel caractère ou % pour correspondre à n'importe quel nombre de caractères.",
    'uk-ua':
      'Можна використовувати _ для відповідності будь-якому одному символу або % для відповідності будь-якій кількості символів.',
    'de-ch':
      'Mit _ kann man ein einzelnes Zeichen finden, mit % eine beliebige Anzahl von Zeichen.',
    'pt-br':
      'Pode-se usar _ para corresponder a qualquer caractere único ou % para corresponder a qualquer número de caracteres.',
    'hr-hr':
      'Može se koristiti _ za pronalaženje bilo kojeg pojedinačnog znaka ili % za pronalaženje bilo kojeg broja znakova.',
    nb: 'Kan bruke _ til å matche et hvilket som helst enkelttegn eller % for å matche et hvilket som helst antall tegn.',
  },
  highlightMatch: {
    'en-us': 'Highlight matched substring',
    'ru-ru': 'Выделить найденную подстроку',
    'es-es': 'Resaltar la subcadena coincidente',
    'fr-fr': 'Mettre en surbrillance la sous-chaîne correspondante',
    'uk-ua': 'Виділіть збіг підрядка',
    'de-ch': 'Übereinstimmende Teilzeichenkette hervorheben',
    'pt-br': 'Destacar a substring correspondente',
    'hr-hr': 'Označi odgovarajući podniz',
    nb: 'Marker samsvarende delstreng',
  },
  languageDescription: {
    'en-us': 'Determines field captions, usage notes and table captions.',
    'ru-ru':
      'Определяет заголовки полей, примечания по использованию и заголовки таблиц.',
    'es-es':
      'Determina los títulos de los campos, las notas de uso y los títulos de las tablas.',
    'fr-fr':
      "Détermine les légendes des champs, les notes d'utilisation et les légendes des tableaux.",
    'uk-ua':
      'Визначає підписи полів, примітки щодо використання та підписи таблиць.',
    'de-ch':
      'Legt Feldbezeichnungen, Nutzungshinweise und Tabellenbezeichnungen fest.',
    'pt-br':
      'Determina legendas de campos, notas de uso e legendas de tabelas.',
    'hr-hr': 'Određuje natpise polja, napomene o korištenju i natpise tablica.',
    nb: 'Bestemmer felttekster, bruksnotater og tabelltekster.',
  },
  showDialogIcon: {
    'en-us': 'Show icon in the header',
    'ru-ru': 'Показать значок в заголовке',
    'es-es': 'Mostrar icono en el encabezado',
    'fr-fr': "Afficher l'icône dans l'en-tête",
    'uk-ua': 'Показати значок у заголовку',
    'de-ch': 'Symbol in der Kopfzeile anzeigen',
    'pt-br': 'Exibir ícone no cabeçalho',
    'hr-hr': 'Prikaži ikonu u zaglavlju',
    nb: 'Vis ikon i overskriften',
  },
  scaleInterface: {
    'en-us': 'Scale Interface',
    'ru-ru': 'Масштабирование интерфейса',
    'es-es': 'Interfaz de escala',
    'fr-fr': 'Interface de balance',
    'uk-ua': 'Інтерфейс масштабу',
    'de-ch': 'Skalenschnittstelle',
    'pt-br': 'Interface de escala',
    'hr-hr': 'Sučelje skale',
    nb: 'Skalagrensesnitt',
  },
  scaleInterfaceDescription: {
    'en-us': 'Scale interface to match font size.',
    'ru-ru': 'Масштабируйте интерфейс в соответствии с размером шрифта.',
    'es-es':
      'Ajusta el tamaño de la interfaz para que coincida con el tamaño de la fuente.',
    'fr-fr': "Adapter l'interface à la taille de la police.",
    'uk-ua': 'Масштабуйте інтерфейс відповідно до розміру шрифту.',
    'de-ch': 'Skalieren Sie die Benutzeroberfläche an die Schriftgröße an.',
    'pt-br': 'Ajuste a interface para corresponder ao tamanho da fonte.',
    'hr-hr': 'Prilagodite sučelje veličini fonta.',
    nb: 'Skaler grensesnittet slik at det samsvarer med skriftstørrelsen.',
  },
  displayAuthor: {
    'en-us': 'Show author in the tree',
    'ru-ru': 'Показать автора в дереве',
    'es-es': 'Mostrar autor en el árbol',
    'fr-fr': "Afficher l'auteur dans l'arbre",
    'uk-ua': 'Показати автора в дереві',
    'de-ch': 'Autor im Baum anzeigen',
    'pt-br': 'Mostrar autor',
    'hr-hr': 'Prikaži autora u stablu',
    nb: 'Vis forfatter i treet',
  },
  displayChronoPeriods: {
    'en-us': 'Show start and end periods',
    'de-ch': 'Start- und Endzeitraum anzeigen',
    'es-es': 'Mostrar los períodos de inicio y fin',
    'fr-fr': 'Afficher les périodes de début et de fin',
    'hr-hr': 'Prikaži početno i završno razdoblje',
    nb: 'Vis start- og sluttperioder',
    'pt-br': 'Mostrar períodos de início e fim',
    'ru-ru': 'Показать начальный и конечный периоды',
    'uk-ua': 'Показати початковий та кінцевий періоди',
  },
  welcomePage: {
    'en-us': 'Home Page',
    'ru-ru': 'Главная страница',
    'es-es': 'Página principal',
    'fr-fr': "Page d'accueil",
    'uk-ua': 'Домашня сторінка',
    'de-ch': 'Startseite',
    'pt-br': 'Página inicial',
    'hr-hr': 'Početna stranica',
    nb: 'Hjemmeside',
  },
  content: {
    'en-us': 'Content',
    'ru-ru': 'Содержание',
    'es-es': 'Contenido',
    'fr-fr': 'Contenu',
    'uk-ua': 'Зміст',
    'de-ch': 'Inhalt',
    'pt-br': 'Contente',
    'hr-hr': 'Sadržaj',
    nb: 'Innhold',
  },
  defaultImage: {
    'en-us': 'Specify Splash',
    'ru-ru': 'Укажите заставку',
    'es-es': 'Especificar Splash',
    'fr-fr': 'Specify Splash',
    'uk-ua': 'Вкажіть логотип',
    'de-ch': 'Splash angeben',
    'pt-br': 'Especificar Splash',
    'hr-hr': 'Navedite prskanje',
    nb: 'Spesifiser Splash',
  },
  critterless: {
    'en-us': 'Specify Splash (No Critters)',
    'ru-ru': 'Без существ',
    'es-es': 'Sin criaturas',
    'fr-fr': 'Sans créatures',
    'uk-ua': 'Без створінь',
    'de-ch': 'Kritterlos',
    'pt-br': 'Sem criaturas',
    'hr-hr': 'Navedite prskanje (bez stvorenja)',
    nb: 'Spesifiser Splash (ingen kryp)',
  },
  customImage: {
    'en-us': 'Custom Image',
    'ru-ru': 'Пользовательское изображение',
    'es-es': 'Imagen personalizada',
    'fr-fr': 'Image personnalisée',
    'uk-ua': 'Спеціальне зображення',
    'de-ch': 'Benutzerdefiniertes Bild',
    'pt-br': 'Imagem personalizada',
    'hr-hr': 'Prilagođena slika',
    nb: 'Tilpasset bilde',
  },
  embeddedWebpage: {
    'en-us': 'Embedded web page',
    'ru-ru': 'Встроенная веб-страница',
    'es-es': 'Página web incrustada',
    'fr-fr': 'Page Web intégrée',
    'uk-ua': 'Вбудована веб-сторінка',
    'de-ch': 'Eingebettete Webseite',
    'pt-br': 'Página web incorporada',
    'hr-hr': 'Ugrađena web stranica',
    nb: 'Innebygd nettside',
  },
  embeddedWebpageDescription: {
    'en-us': 'A URL to a page that would be embedded on the home page:',
    'ru-ru': 'URL-адрес страницы, которая будет встроена на главную страницу:',
    'es-es': 'Una URL a una página que se insertaría en la página de inicio:',
    'fr-fr': "Une URL vers une page qui serait intégrée à la page d'accueil :",
    'uk-ua': 'URL-адреса сторінки, яка буде вбудована на домашній сторінці:',
    'de-ch':
      'Eine URL zu einer Seite, die auf der Startseite eingebettet werden soll:',
    'pt-br': 'Um URL para uma página que seria incorporada na página inicial:',
    'hr-hr': 'URL stranice koja bi bila ugrađena na početnu stranicu:',
    nb: 'En URL til en side som ville bli innebygd på hjemmesiden:',
  },
  behavior: {
    'en-us': 'Behavior',
    'ru-ru': 'Поведение',
    'es-es': 'Comportamiento',
    'fr-fr': 'Comportement',
    'uk-ua': 'Поведінка',
    'de-ch': 'Verhalten',
    'pt-br': 'Comportamento',
    'hr-hr': 'Ponašanje',
    nb: 'Oppførsel',
  },
  inheritance: {
    'en-us': 'Inheritance',
    'de-ch': 'Nachlass',
    'es-es': 'Herencia',
    'fr-fr': 'Héritage',
    'pt-br': 'Herança',
    'ru-ru': 'Наследование',
    'uk-ua': 'Спадщина',
    'hr-hr': 'Nasljeđivanje',
    nb: 'Arv',
  },
  noRestrictionsMode: {
    'en-us': 'No restrictions mode',
    'ru-ru': 'Режим без ограничений',
    'es-es': 'Modo sin restricciones',
    'fr-fr': 'Mode sans restriction',
    'uk-ua': 'Режим без обмежень',
    'de-ch': 'Modus ohne Einschränkungen',
    'pt-br': 'Modo sem restrições',
    'hr-hr': 'Način rada bez ograničenja',
    nb: 'Ingen begrensningsmodus',
  },
  noRestrictionsModeWbDescription: {
    'en-us': 'Allows uploading data to any field in any table.',
    'ru-ru': 'Позволяет загружать данные в любое поле любой таблицы.',
    'es-es': 'Permite cargar datos en cualquier campo de cualquier tabla.',
    'fr-fr':
      "Permet de télécharger des données dans n'importe quel champ de n'importe quelle table.",
    'uk-ua': 'Дозволяє завантажувати дані в будь-яке поле будь-якої таблиці.',
    'de-ch':
      'Ermöglicht das Hochladen von Daten in beliebige Felder beliebiger Tabellen.',
    'pt-br':
      'Permite o carregamento de dados para qualquer campo em qualquer tabela.',
    'hr-hr':
      'Omogućuje prijenos podataka u bilo koje polje u bilo kojoj tablici.',
    nb: 'Tillater opplasting av data til et hvilket som helst felt i en hvilken som helst tabell.',
  },
  noRestrictionsModeQueryDescription: {
    'en-us': 'Allows querying data from any field in any table.',
    'ru-ru': 'Позволяет запрашивать данные из любого поля любой таблицы.',
    'es-es': 'Permite consultar datos de cualquier campo en cualquier tabla.',
    'fr-fr':
      "Permet d'interroger les données de n'importe quel champ de n'importe quelle table.",
    'uk-ua': 'Дозволяє запитувати дані з будь-якого поля будь-якої таблиці.',
    'de-ch':
      'Ermöglicht das Abfragen von Daten aus beliebigen Feldern in beliebigen Tabellen.',
    'pt-br': 'Permite consultar dados de qualquer campo em qualquer tabela.',
    'hr-hr':
      'Omogućuje upite podataka iz bilo kojeg polja u bilo kojoj tablici.',
    nb: 'Tillater spørring av data fra ethvert felt i enhver tabell.',
  },
  noRestrictionsModeWarning: {
    'en-us':
      'WARNING: enabling this may lead to data loss or database corruption. Please make sure you know what you are doing.',
    'ru-ru':
      'ВНИМАНИЕ: включение этой функции может привести к потере данных или повреждению базы данных. Пожалуйста, убедитесь, что вы понимаете, что делаете.',
    'es-es':
      'ADVERTENCIA: habilitar esta opción puede provocar la pérdida de datos o la corrupción de la base de datos. Asegúrese de saber lo que está haciendo.',
    'uk-ua':
      'ПОПЕРЕДЖЕННЯ: увімкнення цієї функції може призвести до втрати даних або пошкодження бази даних. Переконайтеся, що ви знаєте, що робите.',
    'de-ch':
      'WARNUNG: Die Aktivierung dieser Funktion kann zu Datenverlust oder Beschädigung der Datenbank führen. Bitte stellen Sie sicher, dass Sie wissen, was Sie tun.',
    'fr-fr':
      "AVERTISSEMENT : l'activation de cette option peut entraîner une perte de données ou une corruption de la base de données. Veuillez vous assurer que vous savez ce que vous faites.",
    'pt-br':
      'AVISO: habilitar esta opção pode levar à perda de dados ou corrupção do banco de dados. Certifique-se de saber o que está fazendo.',
    'hr-hr':
      'UPOZORENJE: omogućavanje ovoga može dovesti do gubitka podataka ili oštećenja baze podataka. Molimo vas da znate što radite.',
    nb: 'ADVARSEL: Aktivering av dette kan føre til datatap eller ødeleggelse av databasen. Sørg for at du vet hva du gjør.',
  },
  adminsOnlyPreference: {
    'en-us': "You don't have permission to change this option",
    'ru-ru': 'У вас нет разрешения на изменение этой опции.',
    'es-es': 'No tienes permiso para cambiar esta opción.',
    'fr-fr': "Vous n'êtes pas autorisé à modifier cette option",
    'uk-ua': 'Ви не маєте дозволу змінювати цей параметр',
    'de-ch': 'Sie haben keine Berechtigung, diese Option zu ändern.',
    'pt-br': 'Você não tem permissão para alterar esta opção.',
    'hr-hr': 'Nemate dopuštenje za promjenu ove opcije',
    nb: 'Du har ikke tillatelse til å endre dette alternativet',
  },
  stickyScrolling: {
    'en-us': 'Sticky scroll bar',
    'ru-ru': 'Залипающая полоса прокрутки',
    'es-es': 'Barra de desplazamiento fija',
    'fr-fr': 'Barre de défilement collante',
    'uk-ua': 'Липка смуга прокрутки',
    'de-ch': 'Sticky Scroll Bar',
    'pt-br': 'barra de rolagem fixa',
    'hr-hr': 'Ljepljiva traka za pomicanje',
    nb: 'Klebrig rullefelt',
  },
  foreground: {
    'en-us': 'Foreground',
    'ru-ru': 'Передний план',
    'es-es': 'Primer plano',
    'fr-fr': 'Premier plan',
    'uk-ua': 'Передній план',
    'de-ch': 'Vordergrund',
    'pt-br': 'Primeiro plano',
    'hr-hr': 'Prednji plan',
    nb: 'Forgrunn',
  },
  background: {
    'en-us': 'Background',
    'ru-ru': 'Фон',
    'es-es': 'Fondo',
    'fr-fr': 'Arrière-plan',
    'uk-ua': 'Фон',
    'de-ch': 'Hintergrund',
    'pt-br': 'Fundo',
    'hr-hr': 'Pozadina',
    nb: 'Bakgrunn',
  },
  sidebarTheme: {
    'en-us': 'Sidebar theme',
    'de-ch': 'Seitenleisten-Design',
    'es-es': 'Tema de la barra lateral',
    'fr-fr': 'Thème de la barre latérale',
    'ru-ru': 'Тема боковой панели',
    'uk-ua': 'Тема бічної панелі',
    'pt-br': 'Tema da barra lateral',
    'hr-hr': 'Tema bočne trake',
    nb: 'Sidefelt-tema',
  },
  darkForeground: {
    'en-us': 'Foreground (dark theme)',
    'ru-ru': 'Передний план (темная тема)',
    'es-es': 'Primer plano (tema oscuro)',
    'fr-fr': 'Premier plan (thème sombre)',
    'uk-ua': 'Передній план (темна тема)',
    'de-ch': 'Vordergrund (dunkles Design)',
    'pt-br': 'Primeiro plano (tema escuro)',
    'hr-hr': 'Prednji plan (tamna tema)',
    nb: 'Forgrunn (mørkt tema)',
  },
  darkBackground: {
    'en-us': 'Background (dark theme)',
    'ru-ru': 'Фон (темная тема)',
    'es-es': 'Fondo (tema oscuro)',
    'fr-fr': 'Arrière-plan (thème sombre)',
    'uk-ua': 'Фон (темна тема)',
    'de-ch': 'Hintergrund (dunkles Design)',
    'pt-br': 'Plano de fundo (tema escuro)',
    'hr-hr': 'Pozadina (tamna tema)',
    nb: 'Bakgrunn (mørkt tema)',
  },
  accentColor1: {
    'en-us': 'Accent color 1',
    'ru-ru': 'Дополнительный цвет 1',
    'es-es': 'Color de acento 1',
    'fr-fr': "Couleur d'accent 1",
    'uk-ua': 'Акцентний колір 1',
    'de-ch': 'Akzentfarbe 1',
    'pt-br': 'Cor de destaque 1',
    'hr-hr': 'Akcentna boja 1',
    nb: 'Aksentfarge 1',
  },
  accentColor2: {
    'en-us': 'Accent color 2',
    'ru-ru': 'Акцентный цвет 2',
    'es-es': 'Color de acento 2',
    'fr-fr': "Couleur d'accent 2",
    'uk-ua': 'Акцентний колір 2',
    'de-ch': 'Akzentfarbe 2',
    'pt-br': 'Cor de destaque 2',
    'hr-hr': 'Akcentna boja 2',
    nb: 'Aksentfarge 2',
  },
  accentColor3: {
    'en-us': 'Accent color 3',
    'ru-ru': 'Акцентный цвет 3',
    'es-es': 'Color de acento 3',
    'fr-fr': "Couleur d'accent 3",
    'uk-ua': 'Акцентний колір 3',
    'de-ch': 'Akzentfarbe 3',
    'pt-br': 'Cor de destaque 3',
    'hr-hr': 'Akcentna boja 3',
    nb: 'Aksentfarge 3',
  },
  accentColor4: {
    'en-us': 'Accent color 4',
    'ru-ru': 'Дополнительный цвет 4',
    'es-es': 'Color de acento 4',
    'fr-fr': "Couleur d'accent 4",
    'uk-ua': 'Акцентний колір 4',
    'de-ch': 'Akzentfarbe 4',
    'pt-br': 'Cor de destaque 4',
    'hr-hr': 'Akcentna boja 4',
    nb: 'Aksentfarge 4',
  },
  spreadsheet: {
    'en-us': 'Spreadsheet',
    'ru-ru': 'Электронная таблица',
    'es-es': 'Hoja de cálculo',
    'fr-fr': 'Tableur',
    'uk-ua': 'Електронна таблиця',
    'de-ch': 'Kalkulationstabelle',
    'pt-br': 'Planilha',
    'hr-hr': 'Proračunska tablica',
    nb: 'Regneark',
  },
  minSpareRows: {
    'en-us': 'Number of blank rows at the end',
    'ru-ru': 'Количество пустых строк в конце',
    'es-es': 'Número de filas en blanco al final',
    'fr-fr': 'Nombre de lignes vides à la fin',
    'uk-ua': 'Кількість порожніх рядків у кінці',
    'de-ch': 'Anzahl der leeren Zeilen am Ende',
    'pt-br': 'Número de linhas em branco no final',
    'hr-hr': 'Broj praznih redaka na kraju',
    nb: 'Antall blanke rader på slutten',
  },
  autoWrapCols: {
    'en-us': 'Navigate to the other side when reaching the edge column',
    'ru-ru': 'Когда дойдете до краевого столбца, перейдите на другую сторону.',
    'es-es': 'Navegue hacia el otro lado al llegar a la columna del borde.',
    'fr-fr':
      'Naviguez de l’autre côté lorsque vous atteignez la colonne de bord',
    'uk-ua': 'Перейдіть на іншу сторону, коли досягнете краю колонки',
    'de-ch':
      'Wechseln Sie zur anderen Seite, sobald Sie die Randspalte erreicht haben.',
    'pt-br': 'Navegue até o outro lado ao chegar à coluna da borda.',
    'hr-hr': 'Prelazak na drugu stranu prilikom dolaska do rubnog stupca',
    nb: 'Naviger til den andre siden når du når kantkolonnen',
  },
  autoWrapRows: {
    'en-us': 'Navigate to the other side when reaching the edge row',
    'ru-ru': 'Когда дойдете до крайнего ряда, перейдите на другую сторону.',
    'es-es': 'Navegue hacia el otro lado al llegar a la fila del borde.',
    'fr-fr':
      'Naviguez de l’autre côté lorsque vous atteignez la rangée de bord',
    'uk-ua': 'Перейдіть на іншу сторону, коли досягнете крайнього ряду',
    'de-ch':
      'Wechseln Sie zur anderen Seite, sobald Sie die Randreihe erreicht haben.',
    'pt-br': 'Navegue até o outro lado ao chegar à fileira da borda.',
    'hr-hr': 'Prelazak na drugu stranu kada se dođe do rubnog reda',
    nb: 'Naviger til den andre siden når du når kantraden',
  },
  enterBeginsEditing: {
    'en-us': 'Enter key begins editing cell',
    'ru-ru': 'Нажатие клавиши Enter начинает редактирование ячейки',
    'es-es': 'La tecla Enter inicia la edición de la celda.',
    'fr-fr': 'La touche Entrée commence à modifier la cellule',
    'uk-ua': 'Клавіша Enter починає редагування клітинки',
    'de-ch': 'Die Eingabetaste startet die Bearbeitung der Zelle.',
    'pt-br': 'A tecla Enter inicia a edição da célula.',
    'hr-hr': 'Tipka Enter započinje uređivanje ćelije',
    nb: 'Enter-tasten begynner å redigere cellen',
  },
  tabMoveDirection: {
    'en-us': 'Direction of movement when <key>Tab</key> key is pressed',
    'ru-ru': 'Направление движения при нажатии клавиши <key>Tab</key>',
    'es-es': 'Dirección del movimiento al presionar la tecla <key>Tab</key>',
    'fr-fr':
      'Sens de déplacement lorsque la touche <key>Tabulation</key> est enfoncée',
    'uk-ua': 'Напрямок руху при натисканні клавіші <key>Tab</key>',
    'de-ch': 'Bewegungsrichtung bei Drücken der Tabulatortaste <key></key>.',
    'pt-br': 'Direção do movimento quando a tecla <key>Tab</key> é pressionada',
    'hr-hr': 'Smjer kretanja kada je pritisnuta tipka <key>Tab</key>',
    nb: 'Bevegelsesretning når <key>Tab</key>-tasten trykkes',
  },
  tabMoveDirectionDescription: {
    'en-us':
      'You can move in the opposite direction by pressing <key>Shift</key>+<key>Tab</key>.',
    'ru-ru':
      'Для перемещения в противоположном направлении нажмите <key>Shift</key>+<key>Tab</key>.',
    'es-es':
      'Puedes moverte en la dirección opuesta pulsando <key>Shift</key>+<key>Tab</key>.',
    'fr-fr':
      'Vous pouvez vous déplacer dans la direction opposée en appuyant sur <key>Shift</key>+<key>Tab</key>.',
    'uk-ua':
      'Ви можете рухатися в протилежному напрямку, натискаючи <key>Shift</key>+<key>Tab</key>.',
    'de-ch':
      'Sie können in die entgegengesetzte Richtung wechseln, indem Sie <key>Shift</key>+<key>Tab</key> drücken.',
    'pt-br':
      'Você pode se mover na direção oposta pressionando <key>Shift</key>+<key>Tab</key>.',
    'hr-hr':
      'U suprotnom smjeru možete se pomicati pritiskom na <key>Shift</key>+<key>Tab</key>.',
    nb: 'Du kan bevege deg i motsatt retning ved å trykke <key>Shift</key>+<key>Tab</key>.',
  },
  column: {
    'en-us': 'Column',
    'ru-ru': 'Столбец',
    'es-es': 'Columna',
    'fr-fr': 'Colonne',
    'uk-ua': 'Колонка',
    'de-ch': 'Spalte',
    'pt-br': 'Coluna',
    'hr-hr': 'Kolona',
    nb: 'Søyle',
  },
  row: {
    'en-us': 'Row',
    'ru-ru': 'Ряд',
    'es-es': 'Fila',
    'fr-fr': 'Rangée',
    'uk-ua': 'рядок',
    'de-ch': 'Reihe',
    'pt-br': 'Linha',
    'hr-hr': 'Red',
    nb: 'Rad',
  },
  enterMoveDirection: {
    'en-us': 'Direction of movement when <key>Enter</key> key is pressed',
    'ru-ru': 'Направление движения при нажатии клавиши <key>Enter</key>',
    'es-es': 'Dirección del movimiento al pulsar la tecla <key>Enter</key>',
    'uk-ua': 'Напрямок руху, коли натиснуто клавішу <key>Enter</key>',
    'de-ch': 'Bewegungsrichtung beim Drücken der Taste <key>Enter</key>.',
    'fr-fr':
      'Direction du mouvement lorsque la touche <key>Entrer</key> est enfoncée',
    'pt-br':
      'Direção do movimento quando a tecla <key>Enter</key> é pressionada',
    'hr-hr': 'Smjer kretanja kada se pritisne tipka <key>Enter</key>',
    nb: 'Bevegelsesretning når <key>Enter</key>-tasten trykkes',
  },
  enterMoveDirectionDescription: {
    'en-us':
      'You can move in the opposite direction by pressing <key>Shift</key>+<key>Enter</key>.',
    'ru-ru':
      'Вы можете переместиться в противоположном направлении, нажав <key>Shift</key>+<key>Enter</key>.',
    'es-es':
      'Puedes moverte en la dirección opuesta pulsando <key>Shift</key>+<key>Enter</key>.',
    'fr-fr': 'Synonyme couleur.',
    'uk-ua':
      'Ви можете рухатися у протилежному напрямку, натискаючи <key>Shift</key>+<key>Enter</key>.',
    'de-ch':
      'Sie können in die entgegengesetzte Richtung wechseln, indem Sie <key>Shift</key>+<key>Enter</key> drücken.',
    'pt-br':
      'Você pode se mover na direção oposta pressionando <key>Shift</key>+<key>Enter</key>.',
    'hr-hr':
      'U suprotnom smjeru možete se pomicati pritiskom na <key>Shift</key>+<key>Enter</key>.',
    nb: 'Du kan bevege deg i motsatt retning ved å trykke <key>Shift</key>+<key>Enter</key>.',
  },
  filterPickLists: {
    'en-us': 'Filter pick list items',
    'ru-ru': 'Фильтрация элементов выпадающего списка',
    'es-es': 'Filtrar elementos de la lista desplegable',
    'fr-fr': 'Filtrer les éléments de la liste de sélection',
    'uk-ua': 'Фільтр вибору елементів списку',
    'de-ch': 'Filter-Auswahllistenelemente',
    'pt-br': 'Filtrar itens da lista de seleção',
    'hr-hr': 'Filtriranje stavki s popisa za odabir',
    nb: 'Filtrer valglisteelementer',
  },
  exportFileDelimiter: {
    'en-us': 'Export file delimiter',
    'ru-ru': 'Разделитель экспортируемых файлов',
    'es-es': 'Delimitador de archivos de exportación',
    'fr-fr': "Délimiteur de fichier d'exportation",
    'uk-ua': 'Роздільник файлу експорту',
    'de-ch': 'Exportdatei-Trennzeichen',
    'pt-br': 'Delimitador de arquivo de exportação',
    'hr-hr': 'Razdjelnik izvozne datoteke',
    nb: 'Eksporter filskilletegn',
  },
  exportCsvUtf8Bom: {
    'en-us': 'Add UTF-8 BOM to CSV file exports',
    'ru-ru': 'Добавить UTF-8 BOM в экспорт CSV-файла',
    'es-es': 'Agregar BOM UTF-8 a las exportaciones de archivos CSV',
    'fr-fr': 'Ajouter UTF-8 BOM aux exportations de fichiers CSV',
    'uk-ua': 'Додайте специфікацію UTF-8 до експорту файлу CSVу',
    'de-ch': 'UTF-8 BOM zum CSV-Dateiexport hinzufügen',
    'pt-br': 'Adicionar BOM UTF-8 às exportações de arquivos CSV',
    'hr-hr': 'Dodajte UTF-8 BOM u izvoze CSV datoteka',
    nb: 'Legg til UTF-8 BOM i CSV-fileksporter',
  },
  exportCsvUtf8BomDescription: {
    'en-us':
      'Adds a BOM (Byte Order Mark) to exported CSV files to ensure that the file is correctly recognized and displayed by various programs (Excel, OpenRefine, etc.), preventing issues with special characters and formatting.',
    'ru-ru': 'Корректное отображение экспортированных CSV-файлов в Excel.',
    'es-es':
      'Agrega una marca de orden de bytes (BOM, por sus siglas en inglés) a los archivos CSV exportados para garantizar que el archivo sea reconocido y visualizado correctamente por varios programas (Excel, OpenRefine, etc.), evitando problemas con caracteres especiales y formato.',
    'fr-fr':
      "Permet aux exportations de fichiers CSV de s'afficher correctement dans Excel.",
    'uk-ua': 'Змушує експорт файлів CSV правильно відображатися в Excel.',
    'de-ch':
      'Sorgt dafür, dass CSV-Dateiexporte in Excel korrekt angezeigt werden.',
    'pt-br':
      'Adiciona uma BOM (Byte Order Mark) aos arquivos CSV exportados para garantir que o arquivo seja reconhecido e exibido corretamente por diversos programas (Excel, OpenRefine, etc.), evitando problemas com caracteres especiais e formatação.',
    'hr-hr':
      'Dodaje BOM (oznaku redoslijeda bajtova) izvezenim CSV datotekama kako bi se osiguralo da razni programi (Excel, OpenRefine itd.) ispravno prepoznaju i prikazuju datoteku, sprječavajući probleme sa posebnim znakovima i formatiranjem.',
    nb: 'Legger til en BOM (Byte Order Mark) i eksporterte CSV-filer for å sikre at filen gjenkjennes og vises riktig av ulike programmer (Excel, OpenRefine osv.), noe som forhindrer problemer med spesialtegn og formatering.',
  },
  caseSensitive: {
    'en-us': 'Case-sensitive',
    'ru-ru': 'С учетом регистра',
    'es-es': 'Distingue mayúsculas y minúsculas',
    'fr-fr': 'Sensible aux majuscules et minuscules',
    'uk-ua': 'Чутливий до регістру',
    'de-ch': 'Groß-/Kleinschreibung beachten',
    'pt-br': 'Maiúsculas e minúsculas',
    'hr-hr': 'Razlikuje velika i mala slova',
    nb: 'Store/små bokstaver',
  },
  caseInsensitive: {
    'en-us': 'Case-insensitive',
    'ru-ru': 'регистронечувствительный',
    'es-es': 'No distingue entre mayúsculas y minúsculas',
    'fr-fr': 'Insensible à la casse',
    'uk-ua': 'Регістр не враховується',
    'de-ch': 'Groß-/Kleinschreibung wird nicht beachtet',
    'pt-br': 'Sem distinção entre maiúsculas e minúsculas',
    'hr-hr': 'Neosjetljivo na velika i mala slova',
    nb: 'Uavhengig av store og små bokstaver',
  },
  showNoReadTables: {
    'en-us': 'Show tables without "Read" access',
    'ru-ru': 'Показать таблицы без доступа на чтение.',
    'es-es': 'Mostrar tablas sin acceso de "Lectura"',
    'fr-fr': 'Afficher les tableaux sans accès "Lecture"',
    'uk-ua': 'Показувати таблиці без доступу «Читання»',
    'de-ch': 'Tabellen ohne Lesezugriff anzeigen',
    'pt-br': 'Exibir tabelas sem acesso de "Leitura"',
    'hr-hr': 'Prikaži tablice bez pristupa "Čitanje"',
    nb: 'Vis tabeller uten lesetilgang',
  },
  showNoAccessTables: {
    'en-us': 'Show tables without "Create" access',
    'ru-ru': 'Показать таблицы без доступа "Создать".',
    'es-es': 'Mostrar tablas sin acceso "Crear"',
    'fr-fr': 'Afficher les tableaux sans accès "Créer"',
    'uk-ua': 'Показувати таблиці без доступу «Створити»',
    'de-ch': 'Tabellen ohne "Erstellen"-Zugriff anzeigen',
    'pt-br': 'Exibir tabelas sem permissão de "Criar"',
    'hr-hr': 'Prikaži tablice bez pristupa "Izradi"',
    nb: 'Vis tabeller uten «Opprett»-tilgang',
  },
  textAreaAutoGrow: {
    'en-us': 'Text boxes grow automatically',
    'ru-ru': 'Текстовые поля автоматически увеличиваются в размере.',
    'es-es': 'Los cuadros de texto crecen automáticamente',
    'fr-fr': "Les zones de texte s'agrandissent automatiquement",
    'uk-ua': 'Текстові поля збільшуються автоматично',
    'de-ch': 'Textfelder vergrößern sich automatisch',
    'pt-br': 'As caixas de texto aumentam de tamanho automaticamente.',
    'hr-hr': 'Tekstualni okviri se automatski povećavaju',
    nb: 'Tekstbokser vokser automatisk',
  },
  clearQueryFilters: {
    'en-us': 'Reset query filters',
    'ru-ru': 'Сбросить фильтры запроса',
    'es-es': 'Restablecer filtros de consulta',
    'fr-fr': 'Réinitialiser les filtres de requête',
    'uk-ua': 'Скинути фільтри запитів',
    'de-ch': 'Abfragefilter zurücksetzen',
    'pt-br': 'Redefinir filtros de consulta',
    'hr-hr': 'Poništi filtere upita',
    nb: 'Tilbakestill søkefiltre',
  },
  clearQueryFiltersDescription: {
    'en-us': 'Clears all query filters when running a Report from a Form.',
    'de-ch':
      'Löscht alle Abfragefilter beim Ausführen eines Berichts aus einem Formular.',
    'es-es':
      'Borra todos los filtros de consulta al ejecutar un informe desde un formulario.',
    'fr-fr':
      "Efface tous les filtres de requête lors de l'exécution d'un rapport à partir d'un formulaire.",
    'ru-ru': 'Сбрасывает все фильтры запроса при запуске отчета из формы.',
    'uk-ua': 'Очищає всі фільтри запитів під час запуску звіту з форми.',
    'pt-br':
      'Limpa todos os filtros de consulta ao executar um relatório a partir de um formulário.',
    'hr-hr': 'Briše sve filtere upita prilikom pokretanja izvješća iz obrasca.',
    nb: 'Fjerner alle spørringsfiltre når du kjører en rapport fra et skjema.',
  },
  queryParamtersFromForm: {
    'en-us': 'Show query filters when running a Report from a Form',
    'de-ch':
      'Abfragefilter beim Ausführen eines Berichts aus einem Formular anzeigen',
    'es-es':
      'Mostrar filtros de consulta al ejecutar un informe desde un formulario.',
    'fr-fr':
      "Afficher les filtres de requête lors de l'exécution d'un rapport à partir d'un formulaire",
    'ru-ru': 'Отображать фильтры запроса при создании отчета из формы',
    'uk-ua': 'Показувати фільтри запитів під час запуску звіту з форми',
    'pt-br':
      'Exibir filtros de consulta ao executar um relatório a partir de um formulário.',
    'hr-hr': 'Prikaz filtera upita prilikom pokretanja izvješća iz obrasca',
    nb: 'Vis spørringsfiltre når du kjører en rapport fra et skjema',
  },
  autoGrowAutoComplete: {
    'en-us': 'Allow autocomplete to grow as wide as need',
    'ru-ru': 'Разрешите автозаполнению расширяться до необходимой ширины.',
    'es-es':
      'Permitir que la función de autocompletar se expanda tanto como sea necesario.',
    'fr-fr':
      'Sens de déplacement lorsque la touche [X27X]Tabulation[X35X] est enfoncée',
    'uk-ua':
      'Дозволити автозаповнення розширюватися настільки, наскільки потрібно',
    'de-ch':
      'Ermöglichen Sie es der Autovervollständigung, sich so weit auszudehnen, wie nötig',
    'pt-br':
      'Permitir que o preenchimento automático se expanda o quanto for necessário.',
    'hr-hr': 'Omogući automatsko dovršavanje da raste koliko god je potrebno',
    nb: 'Tillat at autofullføring utvides så bredt som nødvendig',
  },
  tableNameInTitle: {
    'en-us': 'Include table name in the browser page title',
    'ru-ru': 'Включите название таблицы в заголовок страницы в браузере.',
    'es-es':
      'Incluir el nombre de la tabla en el título de la página del navegador.',
    'fr-fr':
      'Inclure le nom de la table dans le titre de la page du navigateur',
    'uk-ua': 'Включіть назву таблиці в заголовок сторінки браузера',
    'de-ch': 'Fügen Sie den Tabellennamen in den Seitentitel des Browsers ein.',
    'pt-br': 'Inclua o nome da tabela no título da página do navegador.',
    'hr-hr': 'Uključite naziv tablice u naslov stranice preglednika',
    nb: 'Inkluder tabellnavnet i tittelen på nettlesersiden',
  },
  focusFirstField: {
    'en-us': 'Focus first field',
    'de-ch': 'Fokus zuerst Feld',
    'es-es': 'Primer campo de concentración',
    'fr-fr': 'Concentrez-vous sur le premier champ',
    'ru-ru': 'Сосредоточьтесь на первом поле',
    'uk-ua': 'Перейти до першого поля',
    'pt-br': 'Foque primeiro no campo',
    'hr-hr': 'Fokusiraj prvo polje',
    nb: 'Fokuser først på feltet',
  },
  doubleClickZoom: {
    'en-us': 'Double click to zoom',
    'ru-ru': 'Двойной щелчок для увеличения',
    'es-es': 'Doble clic para ampliar',
    'fr-fr': 'Double-cliquez pour zoomer',
    'uk-ua': 'Двічі клацніть, щоб збільшити',
    'de-ch': 'Doppelklicken zum Vergrößern',
    'pt-br': 'Clique duas vezes para ampliar.',
    'hr-hr': 'Dvaput kliknite za zumiranje',
    nb: 'Dobbeltklikk for å zoome',
  },
  closePopupOnClick: {
    'en-us': 'Close pop-up on outside click',
    'ru-ru': 'Закрыть всплывающее окно при внешнем клике',
    'es-es': 'Cerrar ventana emergente al hacer clic fuera',
    'fr-fr': "Fermer la pop-up lors d'un clic extérieur",
    'uk-ua': 'Закрити спливаюче вікно при зовнішньому клацанні',
    'de-ch': 'Pop-up bei Klick von außen schließen',
    'pt-br': 'Fechar janela pop-up ao clicar fora dela',
    'hr-hr': 'Zatvori skočni prozor vanjskim klikom',
    nb: 'Lukk popup-vinduet ved klikk utenfor',
  },
  animateTransitions: {
    'en-us': 'Animate transitions',
    'ru-ru': 'Анимированные переходы',
    'es-es': 'Animar transiciones',
    'fr-fr': 'Animer les transitions',
    'uk-ua': 'Анімація переходів',
    'de-ch': 'Übergänge animieren',
    'pt-br': 'Animar transições',
    'hr-hr': 'Animiraj prijelaze',
    nb: 'Animer overganger',
  },
  panInertia: {
    'en-us': 'Pan inertia',
    'ru-ru': 'пан инерция',
    'es-es': 'Inercia del pan',
    'fr-fr': 'Inertie du bac',
    'uk-ua': 'Інерція панорами',
    'de-ch': 'Pan-Trägheit',
    'pt-br': 'Inércia de Pan',
    'hr-hr': 'Inercija paniranja',
    nb: 'Pan-inerti',
  },
  mouseDrags: {
    'en-us': 'Mouse drags',
    'ru-ru': 'Перетаскивание мышью',
    'es-es': 'Arrastre del ratón',
    'uk-ua': 'Виділіть відповідний підрядок',
    'de-ch': 'Maus zieht',
    'fr-fr': 'Mettre en surbrillance la sous-chaîne correspondante',
    'pt-br': 'Arrastar o mouse',
    'hr-hr': 'Povlačenje miša',
    nb: 'Musedrag',
  },
  scrollWheelZoom: {
    'en-us': 'Scroll wheel zoom',
    'ru-ru': 'масштабирование с помощью колесика мыши',
    'es-es': 'Zoom con la rueda del ratón',
    'fr-fr': 'Zoom avec la molette de défilement',
    'uk-ua': 'Масштаб колеса прокрутки',
    'de-ch': 'Zoomen mit dem Mausrad',
    'pt-br': 'zoom com a roda de rolagem',
    'hr-hr': 'Zumiranje kotačićem za pomicanje',
    nb: 'Zoom med rullehjul',
  },
  flexibleColumnWidth: {
    'en-us': 'Flexible column width',
    'ru-ru': 'Гибкая ширина колонки',
    'es-es': 'Ancho de columna flexible',
    'fr-fr': 'Largeur de colonne flexible',
    'uk-ua': 'Гнучка ширина колонки',
    'de-ch': 'Flexible Spaltenbreite',
    'pt-br': 'Largura da coluna flexível',
    'hr-hr': 'Fleksibilna širina stupca',
    nb: 'Fleksibel kolonnebredde',
  },
  flexibleSubGridColumnWidth: {
    'en-us': 'Flexible subview grid column width',
    'ru-ru': 'Гибкая ширина столбца сетки подпредставлений',
    'es-es': 'Ancho de columna de cuadrícula de subvista flexible',
    'fr-fr': 'Largeur de colonne flexible de la grille de sous-vue',
    'uk-ua': 'Гнучка ширина стовпця сітки вкладеного перегляду',
    'de-ch': 'Flexible Spaltenbreite des Unteransichtsrasters',
    'pt-br': 'Largura flexível da coluna da grade da subvisualização',
    'hr-hr': 'Fleksibilna širina stupca mreže podprikaza',
    nb: 'Fleksibel kolonnebredde for delvisningsrutenett',
  },
  closeOnEsc: {
    'en-us': 'Close on <key>ESC</key> key press',
    'ru-ru': 'Закрыть при нажатии клавиши <key>ESC</key>',
    'es-es': 'Cerrar al pulsar la tecla <key>ESC</key>',
    'fr-fr': 'Icône et nom de la table',
    'uk-ua': 'Закриття натисканням клавіші <key>ESC</key>',
    'de-ch': 'Schließen durch Drücken der Taste <key>ESC</key>.',
    'pt-br': 'Fechar ao pressionar a tecla <key>ESC</key>',
    'hr-hr': 'Zatvori pritiskom tipke <key>ESC</key>',
    nb: 'Lukk ved å trykke på <key>ESC</key>-tasten',
  },
  closeOnOutsideClick: {
    'en-us': 'Close on outside click',
    'ru-ru': 'Закрыть при внешнем щелчке',
    'es-es': 'Cerrar haciendo clic en el exterior',
    'fr-fr': 'Fermer sur clic extérieur',
    'uk-ua': 'Закрийте зовнішнім клацанням',
    'de-ch': 'Bei Klick von außen schließen',
    'pt-br': 'Fechar ao clicar externamente',
    'hr-hr': 'Zatvori vanjskim klikom',
    nb: 'Lukk ved klikk utvendig',
  },
  specifyNetworkBadge: {
    'en-us': 'Specify Network Badge',
    'ru-ru': 'Укажите сетевой значок',
    'es-es': 'Especificar insignia de red',
    'fr-fr': 'Badge réseau Specify',
    'uk-ua': 'Укажіть значок мережі',
    'de-ch': 'Netzwerkabzeichen angeben',
    'pt-br': 'Especificar o ícone de rede',
    'hr-hr': 'Navedite mrežnu značku',
    nb: 'Spesifiser nettverksmerke',
  },
  useAccessibleFullDatePicker: {
    'en-us': 'Use accessible full date picker',
    'ru-ru': 'Используйте удобный полнофункциональный инструмент выбора даты.',
    'es-es': 'Utilice el selector de fecha completo accesible',
    'fr-fr': 'Utiliser un sélecteur de date complet accessible',
    'uk-ua': 'Використовуйте доступний повний засіб вибору дати',
    'de-ch': 'Verwenden Sie die barrierefreie Datumsauswahl.',
    'pt-br': 'Use o seletor de datas completo e acessível.',
    'hr-hr': 'Koristite pristupačan alat za odabir cijelog datuma',
    nb: 'Bruk tilgjengelig fullstendig datovelger',
  },
  useAccessibleMonthPicker: {
    'en-us': 'Use accessible month picker',
    'ru-ru': 'Используйте удобный инструмент выбора месяца.',
    'es-es': 'Utilice el selector de mes accesible',
    'fr-fr': 'Utiliser le sélecteur de mois accessible',
    'uk-ua': 'Використовуйте доступний засіб вибору місяця',
    'de-ch': 'Nutzen Sie die barrierefreie Monatsauswahl.',
    'pt-br': 'Use o seletor de mês acessível',
    'hr-hr': 'Koristite pristupačan alat za odabir mjeseca',
    nb: 'Bruk tilgjengelig månedsvelger',
  },
  rightAlignNumberFields: {
    'en-us': 'Right-Justify numeric fields',
    'ru-ru': 'Выравнивание числовых полей по правому краю',
    'es-es': 'Alinear a la derecha los campos numéricos',
    'fr-fr': 'Justifier à droite les champs numériques',
    'uk-ua': 'Вирівнювання по правому краю числових полів',
    'de-ch': 'Numerische Felder rechtsbündig ausrichten',
    'pt-br': 'Justificar campos numéricos à direita',
    'hr-hr': 'Desno poravnanje numeričkih polja',
    nb: 'Høyrejusterte numeriske felt',
  },
  roundedCorners: {
    'en-us': 'Rounded corners',
    'ru-ru': 'Закругленные углы',
    'es-es': 'esquinas redondeadas',
    'fr-fr': 'Coins arrondis',
    'uk-ua': 'Заокруглені кути',
    'de-ch': 'Abgerundete Ecken',
    'pt-br': 'Cantos arredondados',
    'hr-hr': 'Zaobljeni kutovi',
    nb: 'Avrundede hjørner',
  },
  showSubviewBorders: {
    'en-us': 'Show borders around subviews',
    'de-ch': 'Rahmen um Unteransichten anzeigen',
    'es-es': 'Mostrar bordes alrededor de las subvistas',
    'fr-fr': 'Afficher les bordures autour des sous-vues',
    'pt-br': 'Exibir bordas ao redor das subvisualizações',
    'ru-ru': 'Отобразить границы вокруг дочерних представлений',
    'uk-ua': 'Показати межі навколо підвидів',
    'hr-hr': 'Prikaži obrube oko podprikaza',
    nb: 'Vis rammer rundt delvisninger',
  },
  limitMaxFieldWidth: {
    'en-us': 'Limit max field width',
    'ru-ru': 'Ограничить максимальную ширину поля',
    'es-es': 'Limitar el ancho máximo del campo',
    'fr-fr': 'Limiter la largeur maximale du champ',
    'uk-ua': 'Обмеження максимальної ширини поля',
    'de-ch': 'Maximale Feldbreite begrenzen',
    'pt-br': 'Limitar a largura máxima do campo',
    'hr-hr': 'Ograniči maksimalnu širinu polja',
    nb: 'Begrens maks feltbredde',
  },
  condenseQueryResults: {
    'en-us': 'Condense query results',
    'ru-ru': 'Сжать результаты запроса',
    'es-es': 'Condensar los resultados de la consulta',
    'fr-fr': 'Condenser les résultats de la requête',
    'uk-ua': 'Згорнути результати запиту',
    'de-ch': 'Suchergebnisse zusammenfassen',
    'pt-br': 'Condensar resultados da consulta',
    'hr-hr': 'Sažeti rezultati upita',
    nb: 'Kondenser spørreresultatene',
  },
  blurContentBehindDialog: {
    'en-us': 'Blur content behind the dialog',
    'ru-ru': 'Размыть содержимое за диалоговым окном',
    'es-es': 'Difuminar el contenido detrás del diálogo',
    'fr-fr': 'Flou le contenu derrière la boîte de dialogue',
    'uk-ua': 'Розмити вміст за діалоговим вікном',
    'de-ch': 'Inhalte hinter dem Dialog verwischen',
    'pt-br': 'Desfocar o conteúdo atrás da caixa de diálogo',
    'hr-hr': 'Zamuti sadržaj iza dijaloga',
    nb: 'Gjør innholdet bak dialogen uklart',
  },
  collectionSortOrderDescription: {
    'en-us': 'This determines the visual order of collections.',
    'ru-ru': 'Это определяет визуальный порядок коллекций.',
    'es-es': 'Esto determina el orden visual de las colecciones.',
    'fr-fr': "Ceci détermine l'ordre visuel des collections.",
    'uk-ua': 'Це визначає візуальний порядок колекцій.',
    'de-ch': 'Dies bestimmt die visuelle Reihenfolge der Sammlungen.',
    'pt-br': 'Isso determina a ordem visual das coleções.',
    'hr-hr': 'Ovo određuje vizualni redoslijed kolekcija.',
    nb: 'Dette bestemmer den visuelle rekkefølgen på samlingene.',
  },
  recordSetRecordToOpen: {
    'en-us': 'Record to open by default',
    'ru-ru': 'Запись, которая будет открыта по умолчанию',
    'es-es': 'Registro que se abrirá por defecto',
    'fr-fr': 'Enregistrement à ouvrir par défaut',
    'uk-ua': 'Запис відкривається за умовчанням',
    'de-ch': 'Standardmäßig zu öffnender Datensatz',
    'pt-br': 'Gravar para abrir por padrão',
    'hr-hr': 'Snimanje za otvaranje prema zadanim postavkama',
    nb: 'Opptak skal åpnes som standard',
  },
  altClickToSupressNewTab: {
    'en-us':
      '<key>{altKeyName:string}</key>+<key>Click</key> to suppress new tab',
    'ru-ru':
      '<key>{altKeyName:string}</key>+<key>Нажмите</key>, чтобы скрыть новую вкладку',
    'es-es':
      '<key>{altKeyName:string}</key>+<key>Haz clic en </key> para suprimir la nueva pestaña',
    'fr-fr':
      '<key>{altKeyName:string}</key>+<key>Cliquez sur</key> pour supprimer le nouvel onglet',
    'uk-ua':
      '<key>{altKeyName:string}</key>+<key>Натисніть </key>, щоб закрити нову вкладку',
    'de-ch':
      '<key>{altKeyName:string}</key>+<key>Klicken Sie auf </key>, um einen neuen Tab zu unterdrücken',
    'pt-br':
      '<key>{altKeyName:string}</key>+<key>Clique</key> para fechar a nova aba',
    'hr-hr':
      '<key>{altKeyName:string}</key>+<key>Kliknite</key> za potiskivanje nove kartice',
    nb: '<key>{altKeyName:string}</key>+<key>Klikk på </key> for å undertrykke ny fane',
  },
  altClickToSupressNewTabDescription: {
    'en-us':
      '<key>{altKeyName:string}</key>+<key>Click</key> a link that usually opens in a new tab to open it in the current tab.',
    'ru-ru':
      '<key>{altKeyName:string}</key>+<key>Щелкните</key> ссылку, которая обычно открывается в новой вкладке, чтобы открыть ее в текущей вкладке.',
    'es-es':
      '<key>{altKeyName:string}</key>+<key>Haz clic</key> en un enlace que normalmente se abre en una pestaña nueva para abrirlo en la pestaña actual.',
    'fr-fr': 'Utiliser le sélecteur de mois accessible.',
    'uk-ua':
      '<key>{altKeyName:string}</key>+<key>Натисніть</key> посилання, яке зазвичай відкривається в новій вкладці, щоб відкрити його в поточній вкладці.',
    'de-ch':
      '<key>{altKeyName:string}</key>+<key>Klicken Sie</key> auf einen Link, der normalerweise in einem neuen Tab geöffnet wird, um ihn im aktuellen Tab zu öffnen.',
    'pt-br':
      '<key>{altKeyName:string}</key>+<key>Clique</key> em um link que normalmente abre em uma nova guia para abri-lo na guia atual.',
    'hr-hr':
      '<key>{altKeyName:string}</key>+<key>Kliknite</key> na poveznicu koja se obično otvara u novoj kartici kako biste je otvorili u trenutnoj kartici.',
    nb: '<key>{altKeyName:string}</key>+<key>Klikk</key> på en lenke som vanligvis åpnes i en ny fane for å åpne den i gjeldende fane.',
  },
  makeFormDialogsModal: {
    'en-us': 'Make form dialogs gray out the background',
    'ru-ru': 'Сделайте так, чтобы диалоговые окна форм были затемнены.',
    'es-es':
      'Hacer que los cuadros de diálogo de formulario tengan el fondo gris.',
    'fr-fr':
      "Rendre les boîtes de dialogue de formulaire grisées sur l'arrière-plan",
    'uk-ua': 'Зробіть діалогові вікна форми сірими фоном',
    'de-ch': 'Formulardialoge sollen einen grauen Hintergrund haben',
    'pt-br':
      'Faça com que os diálogos de formulário deixem o fundo escurecido.',
    'hr-hr': 'Zasivi pozadinu dijaloga obrasca',
    nb: 'Gjør skjemadialoger gråtonet i bakgrunnen',
  },
  autoScrollTree: {
    'en-us': 'Auto scroll tree to focused node',
    'ru-ru': 'Автоматическая прокрутка дерева к сфокусированному узлу',
    'es-es': 'Árbol de desplazamiento automático al nodo enfocado',
    'fr-fr': 'Arbre de défilement automatique vers le nœud ciblé',
    'uk-ua': 'Автоматичне прокручування дерева до виділеного вузла',
    'de-ch': 'Automatisches Scrollen des Baums zum fokussierten Knoten',
    'pt-br': 'A árvore de rolagem automática leva ao nó em foco.',
    'hr-hr': 'Automatsko pomicanje stabla do fokusiranog čvora',
    nb: 'Automatisk rulling av tre til fokusert node',
  },
  sortByField: {
    'en-us': 'Order By Field',
    'de-ch': 'Nach Feld sortieren',
    'es-es': 'Ordenar por campo',
    'fr-fr': 'Trier par champ',
    'pt-br': 'Ordenar por campo',
    'ru-ru': 'Сортировать по полю',
    'uk-ua': 'Сортувати за полем',
    'hr-hr': 'Poredaj po polju',
    nb: 'Sorter etter felt',
  },
  lineWrap: {
    'en-us': 'Line wrap',
    'ru-ru': 'перенос строки',
    'es-es': 'Envoltura de línea',
    'fr-fr': 'Retour à la ligne',
    'uk-ua': 'Обтікання лініями',
    'de-ch': 'Linienumwicklung',
    'pt-br': 'Enrolamento de linha',
    'hr-hr': 'Prelom retka',
    nb: 'Linjebryting',
  },
  indentSize: {
    'en-us': 'Indent size',
    'ru-ru': 'Размер отступа',
    'es-es': 'Tamaño de la sangría',
    'fr-fr': 'Taille du retrait',
    'uk-ua': 'Розмір відступу',
    'de-ch': 'Einbuchtungsgröße',
    'pt-br': 'Tamanho do recuo',
    'hr-hr': 'Veličina uvlake',
    nb: 'Innrykksstørrelse',
  },
  indentWithTab: {
    'en-us': 'Indent with <key>Tab</key>',
    'ru-ru': 'Отступ с помощью <key>Tab</key>',
    'es-es': 'Sangría con <key>Tab</key>',
    'fr-fr': 'Indenter avec <key>Tabulation</key>',
    'uk-ua': 'Відступ із <key>Tab</key>',
    'de-ch': 'Einrücken mit <key>Tab</key>',
    'pt-br': 'Recuar com <key>Tab</key>',
    'hr-hr': 'Uvlačenje s <key>Tab</key>',
    nb: 'Innrykk med <key>Tabulator</key>',
  },
  formHeaderFormat: {
    'en-us': 'Form header format',
    'ru-ru': 'Формат заголовка формы',
    'es-es': 'Formato del encabezado del formulario',
    'fr-fr': "Format d'en-tête de formulaire",
    'uk-ua': 'Формат заголовка форми',
    'de-ch': 'Formularkopfformat',
    'pt-br': 'Formato do cabeçalho do formulário',
    'hr-hr': 'Format zaglavlja obrasca',
    nb: 'Format for skjemaoverskrift',
  },
  iconAndTableName: {
    'en-us': 'Icon and table name',
    'ru-ru': 'Значок и название таблицы',
    'es-es': 'Icono y nombre de la tabla',
    'fr-fr': 'Icône et nom de la table',
    'uk-ua': 'Значок і назва таблиці',
    'de-ch': 'Symbol- und Tabellenname',
    'pt-br': 'Ícone e nome da tabela',
    'hr-hr': 'Ikona i naziv tablice',
    nb: 'Ikon og tabellnavn',
  },
  tableIcon: {
    'en-us': 'Table icon',
    'ru-ru': 'значок таблицы',
    'es-es': 'Icono de mesa',
    'fr-fr': 'Icône de tableau',
    'uk-ua': 'Значок таблиці',
    'de-ch': 'Tischsymbol',
    'pt-br': 'Ícone de tabela',
    'hr-hr': 'Ikona tablice',
    nb: 'Tabellikon',
  },
  maxHeight: {
    'en-us': 'Max height',
    'ru-ru': 'Максимальная высота',
    'es-es': 'Altura máxima',
    'fr-fr': 'hauteur maximum',
    'uk-ua': 'Максимальна висота',
    'de-ch': 'Maximale Höhe',
    'pt-br': 'Altura máxima',
    'hr-hr': 'Maksimalna visina',
    nb: 'Maks høyde',
  },
  autoComplete: {
    'en-us': 'Auto complete',
    'ru-ru': 'Автозаполнение',
    'es-es': 'Autocompletar',
    'fr-fr':
      "Détermine les légendes des champs, les notes d'utilisation et les légendes des tableaux",
    'uk-ua':
      'Визначає підписи полів, примітки щодо використання та підписи таблиць',
    'de-ch': 'Automatische Vervollständigung',
    'pt-br': 'Preenchimento automático',
    'hr-hr': 'Automatsko dovršavanje',
    nb: 'Autofullfør',
  },
  searchCaseSensitive: {
    'en-us': 'Case-sensitive search',
    'es-es': 'Búsqueda que distingue entre mayúsculas y minúsculas',
    'fr-fr': 'Recherche sensible à la casse',
    'uk-ua': 'Пошук з урахуванням регістру',
    'de-ch': 'Suche mit Berücksichtigung der Groß-/Kleinschreibung',
    'ru-ru': 'Поиск с учетом регистра',
    'pt-br': 'Pesquisa que diferencia maiúsculas de minúsculas',
    'hr-hr': 'Pretraživanje koje razlikuje velika i mala slova',
    nb: 'Søk med store og små bokstaver',
  },
  searchField: {
    'en-us': 'Search Field',
    'ru-ru': 'Поле поиска',
    'es-es': 'Campo de búsqueda',
    'fr-fr': 'Champ de recherche',
    'uk-ua': 'Поле пошуку',
    'de-ch': 'Suchfeld',
    'pt-br': 'Campo de pesquisa',
    'hr-hr': 'Polje za pretraživanje',
    nb: 'Søkefelt',
  },
  createInteractions: {
    'en-us': 'Creating an interaction',
    'ru-ru': 'Создание взаимодействия',
    'es-es': 'Crear una interacción',
    'fr-fr': 'Créer une interaction',
    'uk-ua': 'Створення взаємодії',
    'de-ch': 'Eine Interaktion erstellen',
    'pt-br': 'Criar uma interação',
    'hr-hr': 'Stvaranje interakcije',
    nb: 'Å skape en interaksjon',
  },
  tableIdentifier: {
    'en-us': '{tableName:string} identifier',
    'ru-ru': 'Идентификатор {tableName:string}',
    'es-es': 'Identificador de {tableName:string}',
    'fr-fr': 'Identifiant {tableName:string}',
    'uk-ua': 'Ідентифікатор {tableName:string}',
    'de-ch': '{tableName:string}-Identifikator',
    'pt-br': 'Identificador de {tableName:string}',
    'hr-hr': '{tableName:string} identifikator',
    nb: '{tableName:string}-identifikator',
  },
  useSpaceAsDelimiter: {
    'en-us': 'Use space as delimiter',
    'ru-ru': 'Используйте пробел в качестве разделителя.',
    'es-es': 'Utilice el espacio como delimitador.',
    'fr-fr': "Utiliser l'espace comme délimiteur",
    'uk-ua': 'Використовуйте пробіл як роздільник',
    'de-ch': 'Leerzeichen als Trennzeichen verwenden',
    'pt-br': 'Use o espaço como delimitador',
    'hr-hr': 'Koristite razmak kao razdjelnik',
    nb: 'Bruk mellomrom som skilletegn',
  },
  useCommaAsDelimiter: {
    'en-us': 'Use comma as delimiter',
    'ru-ru': 'Используйте запятую в качестве разделителя.',
    'es-es': 'Utilice la coma como delimitador.',
    'fr-fr': 'Utiliser la virgule comme délimiteur',
    'uk-ua': 'Використовуйте кому як роздільник',
    'de-ch': 'Komma als Trennzeichen verwenden',
    'pt-br': 'Use vírgula como delimitador',
    'hr-hr': 'Koristite zarez kao razdjelnik',
    nb: 'Bruk komma som skilletegn',
  },
  useNewLineAsDelimiter: {
    'en-us': 'Use new line as delimiter',
    'ru-ru': 'Используйте новую строку в качестве разделителя.',
    'es-es': 'Utilice una nueva línea como delimitador.',
    'fr-fr': 'Utiliser une nouvelle ligne comme délimiteur',
    'uk-ua': 'Використовуйте новий рядок як роздільник',
    'de-ch': 'Verwenden Sie einen Zeilenumbruch als Trennzeichen.',
    'pt-br': 'Usar nova linha como delimitador',
    'hr-hr': 'Koristite novi redak kao razdjelnik',
    nb: 'Bruk ny linje som skilletegn',
  },
  useCustomDelimiters: {
    'en-us': 'Use custom delimiters',
    'ru-ru': 'Используйте пользовательские разделители',
    'es-es': 'Utilice delimitadores personalizados',
    'fr-fr': 'Utiliser des délimiteurs personnalisés',
    'uk-ua': 'Використовуйте спеціальні роздільники',
    'de-ch': 'Benutzerdefinierte Trennzeichen verwenden',
    'pt-br': 'Use delimitadores personalizados',
    'hr-hr': 'Koristite prilagođene razdjelnike',
    nb: 'Bruk egendefinerte skilletegn',
  },
  useCustomDelimitersDescription: {
    'en-us':
      'A list of delimiters to use, in addition to the ones defined above. Put one delimiter per line.',
    'ru-ru':
      'Список разделителей, которые можно использовать в дополнение к указанным выше. Размещайте по одному разделителю на строку.',
    'es-es':
      'Lista de delimitadores que se pueden usar, además de los definidos anteriormente. Coloque un delimitador por línea.',
    'fr-fr':
      'Une liste de délimiteurs à utiliser, en plus de ceux définis ci-dessus. Mettez un délimiteur par ligne.',
    'uk-ua':
      'Список розділювачів для використання на додаток до визначених вище. Поставте один роздільник на рядок.',
    'de-ch':
      'Eine Liste weiterer Trennzeichen, die zusätzlich zu den oben definierten verwendet werden können. Jedes Trennzeichen gehört in eine eigene Zeile.',
    'pt-br':
      'Uma lista de delimitadores a serem usados, além dos definidos acima. Coloque um delimitador por linha.',
    'hr-hr':
      'Popis razdjelnika koje treba koristiti, uz one definirane gore. Stavite jedan razdjelnik po retku.',
    nb: 'En liste over skilletegn som skal brukes, i tillegg til de som er definert ovenfor. Sett inn ett skilletegn per linje.',
  },
  detectAutomaticallyDescription: {
    'en-us': 'Detect automatically based on catalog number format.',
    'ru-ru': 'Автоматическое определение на основе формата каталожного номера.',
    'es-es': 'Detecta automáticamente según el formato del número de catálogo.',
    'fr-fr':
      'Détecter automatiquement en fonction du format du numéro de catalogue.',
    'uk-ua': 'Визначати автоматично на основі формату номера каталогу.',
    'de-ch': 'Automatische Erkennung anhand des Katalognummernformats.',
    'pt-br': 'Detecção automática com base no formato do número de catálogo.',
    'hr-hr': 'Automatsko otkrivanje na temelju formata kataloškog broja.',
    nb: 'Registrer automatisk basert på katalognummerformat.',
  },
  uniqueness: {
    'en-us': 'Uniqueness',
    'de-ch': 'Einzigartigkeit',
    'es-es': 'Unicidad',
    'fr-fr': 'Unicité',
    'pt-br': 'Singularidade',
    'ru-ru': 'Уникальность',
    'uk-ua': 'Унікальність',
    'hr-hr': 'Jedinstvenost',
    nb: 'Unikhet',
  },
  use: {
    comment: 'Verb',
    'en-us': 'Use',
    'ru-ru': 'Использовать',
    'es-es': 'Usar',
    'fr-fr': 'Utiliser',
    'uk-ua': 'використання',
    'de-ch': 'Verwenden',
    'pt-br': 'Usar',
    'hr-hr': 'Koristiti',
    nb: 'Bruk',
  },
  dontUse: {
    'en-us': 'Don’t use',
    'ru-ru': 'Не использовать',
    'es-es': 'No usar',
    'fr-fr': 'Zoom avec la molette de défilement',
    'uk-ua': 'Масштаб колеса прокрутки',
    'de-ch': 'Nicht verwenden',
    'pt-br': 'Não use',
    'hr-hr': 'Ne koristite',
    nb: 'Ikke bruk',
  },
  position: {
    'en-us': 'Position',
    'es-es': 'Posición',
    'fr-fr': 'Position',
    'ru-ru': 'Позиция',
    'uk-ua': 'Позиція',
    'de-ch': 'Position',
    'pt-br': 'Posição',
    'hr-hr': 'Položaj',
    nb: 'Posisjon',
  },
  top: {
    'en-us': 'Top',
    'es-es': 'Arriba',
    'fr-fr': 'Haut',
    'ru-ru': 'Вершина',
    'uk-ua': 'Топ',
    'de-ch': 'Spitze',
    'pt-br': 'Principal',
    'hr-hr': 'Vrh',
    nb: 'Topp',
  },
  bottom: {
    'en-us': 'Bottom',
    'es-es': 'Abajo',
    'ru-ru': 'Нижний',
    'uk-ua': 'Дно',
    'de-ch': 'Unten',
    'fr-fr': 'Bas',
    'pt-br': 'Fundo',
    'hr-hr': 'Dno',
    nb: 'Bunn',
  },
  left: {
    'en-us': 'Left',
    'es-es': 'Izquierda',
    'fr-fr': 'Gauche',
    'ru-ru': 'Левый',
    'uk-ua': 'Ліворуч',
    'de-ch': 'Links',
    'pt-br': 'Esquerda',
    'hr-hr': 'Lijevo',
    nb: 'Igjen',
  },
  right: {
    'en-us': 'Right',
    'es-es': 'Bien',
    'fr-fr': 'Droite',
    'ru-ru': 'Верно',
    'uk-ua': 'правильно',
    'de-ch': 'Rechts',
    'pt-br': 'Certo',
    'hr-hr': 'Pravo',
    nb: 'Høyre',
  },
  showUnsavedIndicator: {
    'en-us': 'Show unsaved changes indicator',
    'ru-ru': 'Показать индикатор несохраненных изменений',
    'es-es': 'Mostrar indicador de cambios no guardados',
    'fr-fr': "Afficher l'indicateur de modifications non enregistrées",
    'uk-ua': 'Показати індикатор незбережених змін',
    'de-ch': 'Indikator für nicht gespeicherte Änderungen anzeigen',
    'pt-br': 'Mostrar indicador de alterações não salvas',
    'hr-hr': 'Prikaži indikator nespremljenih promjena',
    nb: 'Vis indikator for ulagrede endringer',
  },
  showUnsavedIndicatorDescription: {
    'en-us':
      'Show an "*" in the tab title when there are unsaved changes in the current tab.',
    'es-es':
      'Mostrar un "*" en el título de la pestaña cuando haya cambios sin guardar en la pestaña actual.',
    'fr-fr':
      "Afficher un \"*\" dans le titre de l'onglet lorsqu'il y a des modifications non enregistrées dans l'onglet actuel.",
    'ru-ru':
      'Отображать звездочку "*" в заголовке вкладки, если в текущей вкладке есть несохраненные изменения.',
    'uk-ua':
      'Показувати «*» у заголовку вкладки, якщо в поточній вкладці є незбережені зміни.',
    'de-ch':
      'Zeigen Sie ein "*" im Tab-Titel an, wenn im aktuellen Tab ungespeicherte Änderungen vorhanden sind.',
    'pt-br':
      'Exibir um "*" no título da aba quando houver alterações não salvas na aba atual.',
    'hr-hr':
      'Prikaži "*" u naslovu kartice kada postoje nespremljene promjene u trenutnoj kartici.',
    nb: 'Vis en «*» i fanetittelen når det finnes ulagrede endringer i gjeldende fane.',
  },
  autoPopulateDescription: {
    'en-us':
      'Auto populate the merged record with values from duplicates when opening the merging dialog.',
    'ru-ru':
      'При открытии диалогового окна слияния объединенная запись автоматически заполняется значениями из дубликатов.',
    'de-ch':
      'Beim Öffnen des Zusammenführungsdialogs werden die Werte aus den Duplikaten automatisch in den zusammengeführten Datensatz übernommen.',
    'es-es':
      'Rellene automáticamente el registro combinado con los valores de los duplicados al abrir el cuadro de diálogo de combinación.',
    'fr-fr':
      "Remplir automatiquement l'enregistrement fusionné avec les valeurs des doublons lors de l'ouverture de la boîte de dialogue de fusion.",
    'uk-ua':
      'Автоматичне заповнення об’єднаного запису значеннями з дублікатів під час відкриття діалогового вікна об’єднання.',
    'pt-br':
      'Preencha automaticamente o registro mesclado com os valores dos duplicados ao abrir a caixa de diálogo de mesclagem.',
    'hr-hr':
      'Automatski popuni spojeni zapis vrijednostima iz duplikata prilikom otvaranja dijaloga za spajanje.',
    nb: 'Fyll automatisk ut den sammenslåtte posten med verdier fra duplikater når du åpner sammenslåingsdialogboksen.',
  },
  autoCreateVariants: {
    'en-us': 'Automatically create {agentVariantTable:string} records',
    'ru-ru': 'Автоматически создавать записи {agentVariantTable:string}',
    'de-ch':
      'Automatische Erstellung von {agentVariantTable:string}-Datensätzen',
    'es-es': 'Crear automáticamente registros {agentVariantTable:string}',
    'fr-fr':
      'Créer automatiquement des enregistrements {agentVariantTable:string}',
    'uk-ua': 'Автоматично створювати записи {agentVariantTable:string}',
    'pt-br': 'Criar automaticamente registros {agentVariantTable:string}',
    'hr-hr': 'Automatski stvori {agentVariantTable:string} zapise',
    nb: 'Opprett {agentVariantTable:string}-oppføringer automatisk',
  },
  autoCreateVariantsDescription: {
    'en-us':
      'When merging agents, automatically create {agentVariantTable:string} records based on the variations of first name/last name.',
    'ru-ru':
      'При объединении агентов автоматически создавайте записи {agentVariantTable:string} на основе вариантов имени/фамилии.',
    'de-ch':
      'Beim Zusammenführen von Agenten werden automatisch {agentVariantTable:string}-Datensätze auf Basis der Varianten von Vor- und Nachnamen erstellt.',
    'es-es':
      'Al fusionar agentes, cree automáticamente registros {agentVariantTable:string} basados en las variaciones de nombre/apellido.',
    'fr-fr':
      "Lors de la fusion d'agents, créez automatiquement des enregistrements {agentVariantTable:string} en fonction des variations du prénom/nom.",
    'uk-ua':
      'Під час об’єднання агентів автоматично створювати записи {agentVariantTable:string} на основі варіацій імені/прізвища.',
    'pt-br':
      'Ao mesclar agentes, crie automaticamente registros {agentVariantTable:string} com base nas variações de nome/sobrenome.',
    'hr-hr':
      'Prilikom spajanja agenata, automatski kreirajte {agentVariantTable:string} zapise na temelju varijacija imena/prezimena.',
    nb: 'Når du slår sammen agenter, opprettes {agentVariantTable:string}-oppføringer automatisk basert på variasjonene av fornavn/etternavn.',
  },
  collectionPreferences: {
    'en-us': 'Collection Preferences',
    'de-ch': 'Sammlungspräferenzen',
    'es-es': 'Preferencias de colección',
    'fr-fr': 'Personnalisation',
    'ru-ru': 'Настройки коллекции',
    'uk-ua': 'Налаштування',
    'pt-br': 'Preferências de Coleção',
    'hr-hr': 'Postavke kolekcije',
    nb: 'Samlingsinnstillinger',
  },
  rememberDialogSizes: {
    'en-us': 'Remember dialog window sizes',
    'ru-ru': 'Запомнить размеры диалоговых окон',
    'es-es': 'Recuerde los tamaños de las ventanas de diálogo.',
    'fr-fr': 'Mémoriser les tailles des fenêtres de dialogue',
    'uk-ua': "Запам'ятайте розміри діалогових вікон",
    'de-ch': 'Dialogfenstergrößen merken',
    'pt-br': 'Lembre-se dos tamanhos das janelas de diálogo.',
    'hr-hr': 'Zapamtite veličine dijaloških prozora',
    nb: 'Husk størrelsen på dialogvinduene',
  },
  rememberDialogPositions: {
    'en-us': 'Remember dialog window positions',
    'ru-ru': 'Запомнить положение диалоговых окон',
    'es-es': 'Recuerde las posiciones de las ventanas de diálogo.',
    'fr-fr': 'Mémoriser les positions des fenêtres de dialogue',
    'uk-ua': "Запам'ятовуйте положення діалогового вікна",
    'de-ch': 'Dialogfensterpositionen merken',
    'pt-br': 'Lembre-se das posições das janelas de diálogo.',
    'hr-hr': 'Zapamti položaje dijaloških prozora',
    nb: 'Husk posisjonene til dialogvinduene',
  },
  autoPlayMedia: {
    'en-us': 'Automatically play media',
    'ru-ru': 'Автоматическое воспроизведение медиафайлов',
    'es-es': 'Reproducir contenido multimedia automáticamente',
    'fr-fr': 'Lire automatiquement les médias',
    'uk-ua': 'Автоматичне відтворення медіа',
    'de-ch': 'Medien automatisch abspielen',
    'pt-br': 'Reproduzir mídia automaticamente',
    'hr-hr': 'Automatska reprodukcija medija',
    nb: 'Spill av media automatisk',
  },
  useCustomTooltips: {
    'en-us': 'Use modern tooltips',
    'ru-ru': 'Используйте современные всплывающие подсказки.',
    'es-es': 'Utilice información sobre herramientas moderna.',
    'fr-fr': 'Utiliser des info-bulles modernes',
    'uk-ua': 'Використовуйте сучасні підказки',
    'de-ch': 'Moderne Tooltips verwenden',
    'pt-br': 'Use dicas de ferramentas modernas',
    'hr-hr': 'Koristite moderne opise alata',
    nb: 'Bruk moderne verktøytips',
  },
  alwaysUseQueryBuilder: {
    'en-us': 'Always use query builder search inside of search form',
    'de-ch':
      'Verwenden Sie immer die Abfragegenerator-Suche innerhalb des Suchformulars.',
    'es-es':
      'Utilice siempre el generador de consultas dentro del formulario de búsqueda.',
    'fr-fr':
      'Utilisez toujours la recherche du générateur de requêtes dans le formulaire de recherche',
    'ru-ru':
      'Всегда используйте поиск с помощью построителя запросов внутри формы поиска.',
    'uk-ua': 'Завжди використовуйте пошук конструктора запитів у формі пошуку',
    'pt-br':
      'Sempre utilize o construtor de consultas dentro do formulário de pesquisa.',
    'hr-hr':
      'Uvijek koristite pretragu alata za izradu upita unutar obrasca za pretraživanje',
    nb: 'Bruk alltid søkeverktøyet i søkeskjemaet',
  },
  localizeResourceNames: {
    'en-us': 'Localize the names of recognized app resources',
    'de-ch': 'Lokalisieren Sie die Namen der erkannten App-Ressourcen',
    'es-es':
      'Localizar los nombres de los recursos de la aplicación reconocidos',
    'fr-fr': "Localisez les noms de ressources de l'application reconnues",
    'ru-ru': 'Локализуйте названия распознанных ресурсов приложения.',
    'uk-ua': 'Локалізувати назви розпізнаних ресурсів програми',
    'pt-br': 'Localize os nomes dos recursos de aplicativos reconhecidos.',
    'hr-hr': 'Lokalizirajte nazive prepoznatih resursa aplikacije',
    nb: 'Lokaliser navnene på gjenkjente appressurser',
  },
  splitLongXml: {
    'en-us': 'Split long lines of XML into multiple lines',
    'de-ch': 'Lange XML-Zeilen in mehrere Zeilen aufteilen',
    'es-es': 'Dividir líneas largas de XML en varias líneas.',
    'fr-fr': 'Diviser les longues lignes de XML en plusieurs lignes',
    'ru-ru': 'Разделите длинные строки XML на несколько строк.',
    'uk-ua': 'Розділіть довгі рядки XML на кілька рядків',
    'pt-br': 'Dividir linhas longas de XML em várias linhas.',
    'hr-hr': 'Podijelite duge retke XML-a u više redaka',
    nb: 'Del lange linjer med XML i flere linjer',
  },
  url: {
    'en-us': 'URL',
    'de-ch': 'URL',
    'es-es': 'URL',
    'fr-fr': 'URL',
    'uk-ua': 'URL',
    'ru-ru': 'URL',
    'pt-br': 'URL',
    'hr-hr': 'URL',
    nb: 'URL-adresse',
  },
  pickAttachment: {
    'en-us': 'Pick an attachment',
    'es-es': 'Seleccione un archivo adjunto',
    'fr-fr': 'Choisissez une pièce jointe',
    'ru-ru': 'Выберите вложение',
    'uk-ua': 'Виберіть вкладення',
    'de-ch': 'Wählen Sie einen Anhang aus',
    'pt-br': 'Selecione um anexo',
    'hr-hr': 'Odaberite prilog',
    nb: 'Velg et vedlegg',
  },
  attachmentFailed: {
    'en-us': 'The attachment failed to load.',
    'de-ch': 'Der Anhang konnte nicht geladen werden.',
    'es-es': 'No se pudo cargar el archivo adjunto.',
    'fr-fr': "La pièce jointe n'a pas pu être chargée.",
    'ru-ru': 'Вложение не загрузилось.',
    'uk-ua': 'Не вдалося завантажити вкладений файл.',
    'pt-br': 'O anexo não carregou.',
    'hr-hr': 'Učitavanje privitka nije uspjelo.',
    nb: 'Vedlegget kunne ikke lastes inn.',
  },
  pickImage: {
    'en-us': 'Pick an image',
    'de-ch': 'Wähle ein Bild aus',
    'es-es': 'Elige una imagen',
    'fr-fr': 'Choisissez une image',
    'ru-ru': 'Выберите изображение',
    'uk-ua': 'Виберіть зображення',
    'pt-br': 'Escolha uma imagem',
    'hr-hr': 'Odaberite sliku',
    nb: 'Velg et bilde',
  },
  customLogo: {
    'en-us': 'Expanded Image URL',
    'de-ch': 'Erweiterte Bild-URL',
    'es-es': 'URL de la imagen ampliada',
    'fr-fr': "URL de l'image étendue",
    'ru-ru': 'Расширенная ссылка на изображение',
    'uk-ua': 'Розширена URL-адреса зображення',
    'pt-br': 'URL da imagem expandida',
    'hr-hr': 'URL proširene slike',
    nb: 'Utvidet bilde-URL',
  },
  customLogoCollapsed: {
    'en-us': 'Collapsed Image URL',
    'de-ch': 'URL des zusammengefassten Bildes',
    'es-es': 'URL de la imagen colapsada',
    'fr-fr': "URL de l'image réduite",
    'ru-ru': 'Свернутая ссылка на изображение',
    'uk-ua': 'URL-адреса згорнутого зображення',
    'pt-br': 'URL da imagem recolhida',
    'hr-hr': 'URL sažete slike',
    nb: 'URL-adressen til det skjulte bildet',
  },
  customLogoDescription: {
    'en-us':
      'A URL to an image that would be displayed next to the Specify logo in the navigation menu.',
    'de-ch':
      'Eine URL zu einem Bild, das neben dem Specify-Logo im Navigationsmenü angezeigt werden soll.',
    'es-es':
      'Una URL a una imagen que se mostraría junto al logotipo de Specify en el menú de navegación.',
    'fr-fr':
      'Une URL vers une image qui serait affichée à côté du logo Specify dans le menu de navigation.',
    'ru-ru':
      'URL-адрес изображения, которое будет отображаться рядом с логотипом «Указать» в навигационном меню.',
    'uk-ua':
      'URL-адреса зображення, яке відображатиметься поруч із «Вказати логотип» у меню навігації.',
    'pt-br':
      'Um URL para uma imagem que será exibida ao lado do logotipo "Especificar" no menu de navegação.',
    'hr-hr':
      'URL slike koja bi se prikazivala pored logotipa Navedi u navigacijskom izborniku.',
    nb: 'En URL til et bilde som ville blitt vist ved siden av Spesifiser-logoen i navigasjonsmenyen.',
  },
  showLineNumber: {
    'en-us': 'Show query result line number',
    'de-ch': 'Zeilennummer des Abfrageergebnisses anzeigen',
    'es-es': 'Mostrar número de línea del resultado de la consulta',
    'fr-fr': 'Afficher le numéro de ligne du résultat de la requête',
    'ru-ru': 'Показать номер строки результата запроса',
    'uk-ua': 'Показати номер рядка результату запиту',
    'pt-br': 'Exibir número da linha do resultado da consulta',
    'hr-hr': 'Prikaži broj retka rezultata upita',
    nb: 'Vis linjenummeret i søkeresultatet',
  },
  saveButtonColor: {
    'en-us': 'Save button color',
    'de-ch': 'Farbe der Schaltfläche „Speichern“',
    'es-es': 'Guardar color del botón',
    'fr-fr': 'Couleur du bouton Enregistrer',
    'ru-ru': 'Сохранить цвет кнопки',
    'uk-ua': 'Зберегти колір кнопки',
    'pt-br': 'Cor do botão Salvar',
    'hr-hr': 'Boja gumba za spremanje',
    nb: 'Lagre knappfarge',
  },
  secondaryButtonColor: {
    'en-us': 'Secondary button color',
    'es-es': 'Color del botón secundario',
    'fr-fr': 'Couleur du bouton secondaire',
    'ru-ru': 'Вторичный цвет кнопки',
    'uk-ua': 'Колір вторинної кнопки',
    'de-ch': 'Sekundäre Tastenfarbe',
    'pt-br': 'Cor do botão secundário',
    'hr-hr': 'Boja sekundarnog gumba',
    nb: 'Farge på sekundær knapp',
  },
  secondaryLightButtonColor: {
    'en-us': 'Secondary light button color',
    'de-ch': 'Farbe des sekundären Lichtknopfes',
    'es-es': 'Color del botón de luz secundaria',
    'fr-fr': "Couleur du bouton d'éclairage secondaire",
    'ru-ru': 'Цвет кнопки вторичного освещения',
    'uk-ua': 'Колір вторинної світлової кнопки',
    'pt-br': 'Cor do botão de luz secundário',
    'hr-hr': 'Boja tipke sekundarnog svjetla',
    nb: 'Farge på sekundær lysknapp',
  },
  dangerButtonColor: {
    'en-us': 'Danger button color',
    'de-ch': 'Farbe des Gefahrenknopfes',
    'es-es': 'Color del botón de peligro',
    'fr-fr': 'Couleur du bouton de danger',
    'ru-ru': 'Цвет кнопки «Опасность»',
    'uk-ua': 'Колір кнопки небезпеки',
    'pt-br': 'Cor do botão de perigo',
    'hr-hr': 'Boja gumba za opasnost',
    nb: 'Farge på fareknappen',
  },
  infoButtonColor: {
    'en-us': 'Info button color',
    'de-ch': 'Farbe der Info-Schaltfläche',
    'es-es': 'Color del botón de información',
    'fr-fr': "Couleur du bouton d'information",
    'ru-ru': 'Цвет кнопки информации',
    'uk-ua': 'Колір інформаційної кнопки',
    'pt-br': 'Cor do botão de informações',
    'hr-hr': 'Boja gumba s informacijama',
    nb: 'Farge på infoknappen',
  },
  warningButtonColor: {
    'en-us': 'Warning button color',
    'de-ch': 'Farbe der Warnschaltfläche',
    'es-es': 'Color del botón de advertencia',
    'fr-fr': "Couleur du bouton d'avertissement",
    'ru-ru': 'Цвет кнопки предупреждения',
    'uk-ua': 'Колір кнопки попередження',
    'pt-br': 'Cor do botão de aviso',
    'hr-hr': 'Boja gumba za upozorenje',
    nb: 'Farge på varselknappen',
  },
  successButtonColor: {
    'en-us': 'Success button color',
    'de-ch': 'Farbe der Erfolgsschaltfläche',
    'es-es': 'Color del botón de éxito',
    'fr-fr': 'Couleur du bouton de réussite',
    'ru-ru': 'Цвет кнопки «Успех»',
    'uk-ua': 'Колір кнопки успіху',
    'pt-br': 'Cor do botão de sucesso',
    'hr-hr': 'Boja gumba za uspjeh',
    nb: 'Farge på suksessknappen',
  },
  openAsReadOnly: {
    'en-us': 'Open all records in read-only mode',
    'de-ch': 'Alle Datensätze im Nur-Lese-Modus öffnen',
    'es-es': 'Abrir todos los registros en modo de solo lectura.',
    'fr-fr': 'Ouvrir tous les enregistrements en mode lecture seule',
    'ru-ru': 'Открыть все записи в режиме только для чтения',
    'uk-ua': 'Відкрити всі записи в режимі лише для читання',
    'pt-br': 'Abrir todos os registros em modo somente leitura',
    'hr-hr': 'Otvori sve zapise u načinu rada samo za čitanje',
    nb: 'Åpne alle poster i skrivebeskyttet modus',
  },
  displayBasicView: {
    'en-us': 'Display basic view',
    'de-ch': 'Grundlegende Ansicht anzeigen',
    'es-es': 'Mostrar vista básica',
    'fr-fr': 'Afficher la vue de base',
    'ru-ru': 'Отображение базового вида',
    'uk-ua': 'Відобразити базовий вигляд',
    'pt-br': 'Exibir visualização básica',
    'hr-hr': 'Prikaži osnovni prikaz',
    nb: 'Vis grunnleggende visning',
  },
  showComparisonOperatorsForString: {
    'en-us': 'Show comparison operators for text-based fields',
    'de-ch': 'Vergleichsoperatoren für textbasierte Felder anzeigen',
    'es-es': 'Mostrar operadores de comparación para campos basados en texto',
    'fr-fr': 'Afficher les opérateurs de comparaison pour les champs textuels',
    'pt-br': 'Exibir operadores de comparação para campos baseados em texto.',
    'ru-ru': 'Показать операторы сравнения для текстовых полей',
    'uk-ua': 'Показати оператори порівняння для текстових полів',
    'hr-hr': 'Prikaži operatore usporedbe za tekstualna polja',
    nb: 'Vis sammenligningsoperatorer for tekstbaserte felt',
  },
  showComparisonOperatorsDescription: {
    'en-us':
      'Allows the following filters to apply to text fields: Greater Than, Less Than, Greater Than or Equal to, and Less Than or Equal to',
    'de-ch':
      'Ermöglicht die Anwendung der folgenden Filter auf Textfelder: Größer als, Kleiner als, Größer oder gleich und Kleiner oder gleich',
    'es-es':
      'Permite aplicar los siguientes filtros a los campos de texto: Mayor que, Menor que, Mayor o igual que y Menor o igual que.',
    'fr-fr':
      "Permet d'appliquer les filtres suivants aux champs de texte : Supérieur à, Inférieur à, Supérieur ou égal à et Inférieur ou égal à",
    'pt-br':
      'Permite que os seguintes filtros sejam aplicados aos campos de texto: Maior que, Menor que, Maior ou igual a e Menor ou igual a.',
    'ru-ru':
      'Позволяет применять к текстовым полям следующие фильтры: Больше, Меньше, Больше или равно и Меньше или равно',
    'uk-ua':
      'Дозволяє застосовувати до текстових полів такі фільтри: «Більше ніж», «Менше ніж», «Більше або дорівнює» та «Менше або дорівнює»',
    'hr-hr':
      'Omogućuje primjenu sljedećih filtera na tekstualna polja: Veće od, Manje od, Veće ili jednako i Manje od ili jednako',
    nb: 'Tillater at følgende filtre brukes på tekstfelt: Større enn, Mindre enn, Større enn eller lik og Mindre enn eller lik',
  },
  basicView: {
    'en-us': 'Basic view',
    'de-ch': 'Grundansicht',
    'es-es': 'Vista básica',
    'fr-fr': 'Vue de base',
    'ru-ru': 'Базовый вид',
    'uk-ua': 'Основний вигляд',
    'pt-br': 'Visão básica',
    'hr-hr': 'Osnovni prikaz',
    nb: 'Grunnleggende visning',
  },
  detailedView: {
    'en-us': 'Detailed view',
    'de-ch': 'Detailansicht',
    'es-es': 'Vista detallada',
    'fr-fr': 'Vue détaillée',
    'ru-ru': 'Детальный вид',
    'uk-ua': 'Детальний вигляд',
    'pt-br': 'Visão detalhada',
    'hr-hr': 'Detaljan prikaz',
    nb: 'Detaljert visning',
  },
  attachmentPreviewMode: {
    'en-us': 'Attachment preview mode',
    'de-ch': 'Anhangsvorschaumodus',
    'es-es': 'Modo de vista previa del archivo adjunto',
    'fr-fr': 'Mode aperçu des pièces jointes',
    'ru-ru': 'режим предварительного просмотра вложений',
    'uk-ua': 'Режим попереднього перегляду вкладених файлів',
    'pt-br': 'Modo de pré-visualização de anexos',
    'hr-hr': 'Način pregleda privitka',
    nb: 'Forhåndsvisningsmodus for vedlegg',
  },
  fullResolution: {
    'en-us': 'Full Resolution',
    'de-ch': 'Volle Auflösung',
    'es-es': 'Resolución completa',
    'fr-fr': 'Résolution maximale',
    'ru-ru': 'Полное разрешение',
    'uk-ua': 'Повна роздільна здатність',
    'pt-br': 'Resolução completa',
    'hr-hr': 'Puna rezolucija',
    nb: 'Full oppløsning',
  },
  thumbnail: {
    'en-us': 'Thumbnail',
    'de-ch': 'Miniaturansicht',
    'es-es': 'Uña del pulgar',
    'fr-fr': 'Vignette',
    'ru-ru': 'Миниатюра',
    'uk-ua': 'Мініатюра',
    'pt-br': 'Miniatura',
    'hr-hr': 'Sličica',
    nb: 'Miniatyrbilde',
  },
  addSearchBarHomePage: {
    'en-us': 'Add Search Bar on home page',
    'de-ch': 'Suchleiste auf der Startseite hinzufügen',
    'es-es': 'Agregar barra de búsqueda en la página de inicio',
    'fr-fr': "Ajouter une barre de recherche sur la page d'accueil",
    'ru-ru': 'Добавить строку поиска на главную страницу',
    'uk-ua': 'Додайте рядок пошуку на головну сторінку',
    'pt-br': 'Adicionar barra de pesquisa na página inicial',
    'hr-hr': 'Dodajte traku za pretraživanje na početnu stranicu',
    nb: 'Legg til søkefelt på hjemmesiden',
  },
  inheritanceCatNumberPrefDescription: {
    'en-us':
      'Only {collectionObjectTableName: string} records without a {catalogNumberFieldName: string} will inherit one from the primary {collectionObjectTableName: string} in a {collectionObjectGroupTableName: string}.',
    'de-ch':
      'Nur {collectionObjectTableName: string}-Datensätze ohne {catalogNumberFieldName: string} erben einen solchen vom primären {collectionObjectTableName: string} in einer {collectionObjectGroupTableName: string}.',
    'es-es':
      'Solo los registros {collectionObjectTableName: string} que no tengan un {catalogNumberFieldName: string} heredarán uno del {collectionObjectTableName: string} principal en un {collectionObjectGroupTableName: string}.',
    'fr-fr':
      "Seuls les enregistrements {collectionObjectTableName: string} sans {catalogNumberFieldName: string} hériteront d'un {collectionObjectTableName: string} principal dans un {collectionObjectGroupTableName: string}.",
    'pt-br':
      'Somente os registros {collectionObjectTableName: string} sem um {catalogNumberFieldName: string} herdarão um do {collectionObjectTableName: string} primário em um {collectionObjectGroupTableName: string}.',
    'ru-ru':
      'Только записи {collectionObjectTableName: string}, не имеющие поля {catalogNumberFieldName: string}, унаследуют его от основного поля {collectionObjectTableName: string} в таблице {collectionObjectGroupTableName: string}.',
    'uk-ua':
      'Тільки записи {collectionObjectTableName: string} без {catalogNumberFieldName: string} успадкують його від основного {collectionObjectTableName: string} в {collectionObjectGroupTableName: string}.',
    'hr-hr':
      'Samo zapisi {collectionObjectTableName: string} bez {catalogNumberFieldName: string} naslijedit će jedan od primarnog {collectionObjectTableName: string} u {collectionObjectGroupTableName: string}.',
    nb: 'Bare {collectionObjectTableName: string}-poster uten en {catalogNumberFieldName: string} vil arve en fra den primære {collectionObjectTableName: string} i en {collectionObjectGroupTableName: string}.',
  },
  inheritanceCatNumberPref: {
    'en-us':
      'Enable {catalogNumberFieldName: string} inheritance for {collectionObjectGroupTableName: string} siblings',
    'de-ch':
      'Aktivieren Sie die Vererbung von {catalogNumberFieldName: string} für die Geschwistertabellen {collectionObjectGroupTableName: string}.',
    'es-es':
      'Habilitar la herencia de {catalogNumberFieldName: string} para los elementos hermanos de {collectionObjectGroupTableName: string}',
    'fr-fr':
      "Activer l'héritage de {catalogNumberFieldName: string} pour les éléments frères de {collectionObjectGroupTableName: string}",
    'pt-br':
      'Habilitar herança de {catalogNumberFieldName: string} para itens irmãos de {collectionObjectGroupTableName: string}',
    'ru-ru':
      'Включить наследование {catalogNumberFieldName: string} для соседних элементов {collectionObjectGroupTableName: string}',
    'uk-ua':
      'Увімкнути успадкування основного {catalogNumberFieldName: string} його порожнім братам і сестрам',
    'hr-hr':
      'Omogući nasljeđivanje {catalogNumberFieldName: string} za {collectionObjectGroupTableName: string} objekte iste klase',
    nb: 'Aktiver arv av {catalogNumberFieldName: string} for søsken i {collectionObjectGroupTableName: string}',
  },
  inheritanceCatNumberParentCOPref: {
    'en-us':
      'Enable {catalogNumberFieldName: string} inheritance for {componentTableName: string} records',
    'de-ch':
      'Aktivieren Sie die Vererbung von {catalogNumberFieldName: string} für {componentTableName: string}-Datensätze.',
    'es-es':
      'Habilitar la herencia de {catalogNumberFieldName: string} para los registros {componentTableName: string}',
    'fr-fr':
      "Activer l'héritage {catalogNumberFieldName: string} pour les enregistrements {componentTableName: string}",
    'pt-br':
      'Habilitar herança de {catalogNumberFieldName: string} para registros de {componentTableName: string}',
    'ru-ru':
      'Включить наследование {catalogNumberFieldName: string} для записей {componentTableName: string}',
    'uk-ua':
      'Увімкніть успадкування {catalogNumberFieldName: string} для записів {componentTableName: string}',
    'hr-hr':
      'Omogući nasljeđivanje {catalogNumberFieldName: string} za zapise {componentTableName: string}',
    nb: 'Aktiver arv av {catalogNumberFieldName: string} for poster etter {componentTableName: string}',
  },
  uniqueCatNumberAcrossCompAndCo: {
    'en-us':
      'Enforce uniqueness for the {catalogNumberFieldName: string} field across {componentTableName: string} and {collectionObjectTableName: string}.',
    'de-ch':
      'Erzwinge die Eindeutigkeit des Feldes {catalogNumberFieldName: string} in den Tabellen {componentTableName: string} und {collectionObjectTableName: string}.',
    'es-es':
      'Garantizar la unicidad del campo {catalogNumberFieldName: string} en {componentTableName: string} y {collectionObjectTableName: string}.',
    'fr-fr':
      "Imposer l'unicité du champ {catalogNumberFieldName: string} entre {componentTableName: string} et {collectionObjectTableName: string}.",
    'pt-br':
      'Garanta a unicidade do campo {catalogNumberFieldName: string} em {componentTableName: string} e {collectionObjectTableName: string}.',
    'ru-ru':
      'Обеспечьте уникальность поля {catalogNumberFieldName: string} для полей {componentTableName: string} и {collectionObjectTableName: string}.',
    'uk-ua':
      'Забезпечити унікальність для поля {catalogNumberFieldName: string} у {componentTableName: string} та {collectionObjectTableName: string}.',
    'hr-hr':
      'Nametnite jedinstvenost za polje {catalogNumberFieldName: string} u {componentTableName: string} i {collectionObjectTableName: string}.',
    nb: 'Håndhev unikhet for feltet {catalogNumberFieldName: string} på tvers av {componentTableName: string} og {collectionObjectTableName: string}.',
  },
  scopeEntireTablePicklists: {
    'en-us': 'Scope "Entire Table" Picklists',
    'de-ch': 'Bereich „Gesamte Tabelle“ Auswahllisten',
    'es-es': 'Listas desplegables de alcance "Tabla completa"',
    'fr-fr': 'Perimètre des picklists de type "table entière"',
    'pt-br': 'Escopo "Tabela Inteira" Listas de seleção',
    'ru-ru': 'Область действия: Списки выбора "Вся таблица"',
    'uk-ua': 'Списки вибору «Уся таблиця»',
    'hr-hr': 'Popisi za odabir "Cijela tablica"',
    nb: 'Omfang «Hele tabellen»-valglister',
  },
  scopeEntireTablePicklistsDescription: {
    'en-us':
      'If enabled, picklists of type "Entire Table" will only show items that are in use within the current collection.',
    'de-ch':
      'Wenn diese Option aktiviert ist, werden in Auswahllisten vom Typ „Gesamte Tabelle“ nur Elemente angezeigt, die in der aktuellen Sammlung verwendet werden.',
    'es-es':
      'Si está habilitada, las listas desplegables de tipo "Tabla completa" solo mostrarán los elementos que se estén utilizando en la colección actual.',
    'fr-fr':
      "Si cette option est activée, les picklists de type « table entière » n'afficheront que les éléments utilisés dans la collection actuelle.",
    'pt-br':
      'Se ativada, a opção "Lista de opções" do tipo "Tabela inteira" exibirá apenas os itens que estão em uso na coleção atual.',
    'ru-ru':
      'Если эта функция включена, в выпадающих списках типа «Вся таблица» будут отображаться только элементы, используемые в текущей коллекции.',
    'uk-ua':
      'Якщо ввімкнено, у списках вибору типу «Уся таблиця» відображатимуться лише ті елементи, які використовуються в поточній колекції.',
    'hr-hr':
      'Ako je omogućeno, popisi za odabir tipa "Cijela tablica" prikazivat će samo stavke koje se koriste unutar trenutne kolekcije.',
    nb: 'Hvis aktivert, vil valglister av typen «Hele tabellen» bare vise elementer som er i bruk i gjeldende samling.',
  },
  inheritanceCatNumberParentCOPrefDescription: {
    'en-us':
      'Only {componentTableName: string} records without a {catalogNumberFieldName: string} will inherit one from its parent {collectionObjectTableName: string}.',
    'de-ch':
      'Nur Datensätze mit dem Namen {componentTableName: string}, die kein {catalogNumberFieldName: string} aufweisen, erben ein solches von ihrem übergeordneten Datensatz {collectionObjectTableName: string}.',
    'es-es':
      'Solo los registros {componentTableName: string} que no tengan un {catalogNumberFieldName: string} heredarán uno de su padre {collectionObjectTableName: string}.',
    'fr-fr':
      'Seuls les enregistrements {componentTableName: string} sans {catalogNumberFieldName: string} hériteront de celui de leur {collectionObjectTableName: string} parent.',
    'pt-br':
      'Somente os registros {componentTableName: string} sem um {catalogNumberFieldName: string} herdarão um de seu pai {collectionObjectTableName: string}.',
    'ru-ru':
      'Только записи {componentTableName: string}, не имеющие поля {catalogNumberFieldName: string}, унаследуют его от родительской таблицы {collectionObjectTableName: string}.',
    'uk-ua':
      'Тільки записи {componentTableName: string} без {catalogNumberFieldName: string} успадкують його від батьківського {collectionObjectTableName: string}.',
    'hr-hr':
      'Samo će zapisi {componentTableName: string} bez {catalogNumberFieldName: string} naslijediti jedan od svog roditelja {collectionObjectTableName: string}.',
    nb: 'Bare {componentTableName: string}-poster uten en {catalogNumberFieldName: string} vil arve en fra den overordnede {collectionObjectTableName: string}.',
  },
} as const);
